import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import { TopNav } from "@/components/Layout";
import { api, API_BASE, getToken } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { computeHealthScore, scoreBand, statusColor, parseRange, rangePosition } from "@/lib/health";
import {
  AlertCircle, TrendingUp, Play, Pause, MessageCircle, ArrowRight, Loader2, Send, Sparkles,
  Utensils, Activity, ShieldAlert, FileText, ClipboardCheck, LineChart as LineChartIcon,
  X, ChevronRight, Calendar, Beaker, Droplet, Heart, Share2, Download, Copy, Check,
  Phone, Mail, Globe, Headphones, PhoneCall, AlertTriangle, Clock, MapPin, Instagram, Facebook, Linkedin, Youtube, Twitter, Upload,
} from "lucide-react";
import { toast } from "sonner";
import ReviewWizard from "@/components/ReviewWizard";
import {
  LineChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis, ReferenceArea, CartesianGrid,
} from "recharts";
import { QRCodeSVG } from "qrcode.react";
import { PrintDocument } from "./pdf/PrintDocument";
import { downloadReportWithCover } from "./pdf/downloadWithCover";
import { BookNowButton } from "@/components/booking/BookNowButton";
import ReportIntegrityFooter from "@/components/report/ReportIntegrityFooter";
import InteractiveBodyMap from "@/components/report/InteractiveBodyMap";
import { useSiteSettings } from "@/lib/settings";

const iconMap = {
  Thyroid: "🦋", Liver: "🫁", Kidney: "🫘", "Blood Sugar": "🩸", Heart: "❤️",
  Calcium: "🦴", "Vitamin D": "☀️", "Vitamin B12": "💊", Hemoglobin: "💪", "WBC Count": "🧪",
};

export default function SmartReport() {
  const { id } = useParams();
  const [report, setReport] = useState(null);
  const [history, setHistory] = useState([]);
  const [lab, setLab] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedBio, setSelectedBio] = useState(null);
  const [bioFilter, setBioFilter] = useState(null); // { label, names: Set } | null
  const [chatOpen, setChatOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  const audioRef = useRef(null);
  const printRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const [lang, setLang] = useState("en");
  const { platform } = useSiteSettings();
  // Regional language derived from platform default + lab override + kill-switch.
  const regionalLang = lab?.regional_language || platform?.default_regional_language || "hi";
  const listenGlobally = platform?.listen_enabled !== false;
  const listenDisabledLabs = platform?.listen_disabled_labs || [];
  const listenEnabled = listenGlobally && !(report?.lab_id && listenDisabledLabs.includes(report.lab_id));

  const LANG_LABELS_NATIVE = {
    en: "EN", hi: "हिं", pa: "ਪੰ", gu: "ગુ", bn: "বাং", mr: "मरा",
  };

  // Force Recharts (ResponsiveContainer) to re-measure right before print so SVGs fit inside their cards.
  useEffect(() => {
    const trigger = () => window.dispatchEvent(new Event("resize"));
    window.addEventListener("beforeprint", trigger);
    return () => window.removeEventListener("beforeprint", trigger);
  }, []);

  // Meaningful PDF filename in the format PatientName_LabName_ReportID
  // (react-to-print & the browser's Save-As dialog use this as the default name).
  const pdfFilename = useMemo(() => {
    const slug = (s) => String(s || "").replace(/[^A-Za-z0-9]/g, "");
    const patient = slug(report?.patient_name) || "Patient";
    const labName = slug(lab?.name) || "EFGH";
    const shortId = String(report?.id || id || "").slice(0, 6).toUpperCase() || "REPORT";
    return `${patient}_${labName}_${shortId}`;
  }, [report?.patient_name, lab?.name, report?.id, id]);

  const reactPrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: pdfFilename,
    onPrintError: (loc, err) => {
      console.error("react-to-print failed:", loc, err);
      toast.error("Print failed — falling back to browser print");
      window.print();
    },
  });

  // Cover-page feature: super_admin enables per-lab + lab admin uploads a PDF.
  // When both conditions are met, we override the default print flow with a
  // client-side merge that prepends the cover PDF as page 1.
  const coverPageEnabledLabs = platform?.cover_page_enabled_labs || [];
  const coverPageEnabled = report?.lab_id && coverPageEnabledLabs.includes(report.lab_id) && !!lab?.cover_page_url;
  const [pdfBusy, setPdfBusy] = useState(false);

  /**
   * PDF download strategy:
   *   1. **Server-side (primary)** — hit `/api/reports/{id}/pdf?auth={token}`. Backend uses
   *      Playwright + headless Chromium to render the exact PrintDocument surface, producing
   *      a native vector PDF with selectable text. Cover-page merging (if applicable) is done
   *      server-side too.
   *   2. **Client-side cover merge (fallback)** — if the server fails and a cover PDF is
   *      configured, fall back to the html2canvas + pdf-lib client merge.
   *   3. **Browser print (last resort)** — `react-to-print` invokes the browser print dialog.
   */
  const handlePrint = async () => {
    if (!printRef.current) return toast.error("Report not ready yet");
    setPdfBusy(true);
    const t = toast.loading("Preparing PDF…");
    try {
      const token = getToken();
      const url = `${API_BASE}/reports/${id}/pdf?auth=${encodeURIComponent(token)}`;
      const resp = await fetch(url, { method: "GET" });
      if (!resp.ok) throw new Error(`Server PDF ${resp.status}`);
      const blob = await resp.blob();
      const dlUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = dlUrl;
      a.download = `${pdfFilename}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(dlUrl), 5000);
      toast.success("PDF downloaded", { id: t });
      setPdfBusy(false);
      return;
    } catch (err) {
      console.warn("[PDF] server render failed, falling back to client-side:", err);
    }

    // Fallback path — client-side cover merge (or plain react-to-print)
    if (coverPageEnabled) {
      try {
        const backend = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
        const coverUrl = lab.cover_page_url.startsWith("http")
          ? lab.cover_page_url
          : `${backend}${lab.cover_page_url}`;
        await downloadReportWithCover({
          docEl: printRef.current,
          letterhead: lab?.letterhead || {},
          coverAbsoluteUrl: coverUrl,
          filename: pdfFilename,
        });
        toast.success("PDF downloaded", { id: t });
      } catch (e) {
        console.error("Client cover-merge failed:", e);
        toast.error("PDF export failed — opening print dialog", { id: t });
        try {
          const p = reactPrint();
          if (p && typeof p.then === "function") p.catch(() => window.print());
        } catch { window.print(); }
      }
    } else {
      toast.dismiss(t);
      try {
        const p = reactPrint();
        if (p && typeof p.then === "function") p.catch(() => window.print());
      } catch { window.print(); }
    }
    setPdfBusy(false);
  };

  const fetchReport = async () => {
    try {
      const r = await api.get(`/reports/${id}`);
      setReport(r.data);
      if (r.data?.lab_id) {
        try {
          const lr = await api.get(`/labs/${r.data.lab_id}`);
          setLab(lr.data);
        } catch { /* ignore */ }
      }
      return r.data;
    } catch { toast.error("Report not found"); }
    finally { setLoading(false); }
  };

  const fetchHistory = async () => {
    try {
      const r = await api.get(`/reports/${id}/history`);
      setHistory(r.data.reports || []);
    } catch { /* ignore */ }
  };

  useEffect(() => { fetchReport(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!report) return;
    if (report.status === "processing" || report.status === "pending" || report.status === "pending_review") {
      const t = setInterval(fetchReport, 5000);
      return () => clearInterval(t);
    }
    if (report.status === "ready" || report.status === "published") fetchHistory();
  }, [report?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const smart = report?.smart_report;
  // Prefer the backend-computed weighted health score. Falls back to the local heuristic only if backend didn't compute one.
  const healthScore = useMemo(() => {
    if (typeof report?.health_score === "number") return report.health_score;
    return computeHealthScore(smart);
  }, [smart, report?.health_score]);
  // scoreBand() returns an object {label, color}. If backend gives a plain string, keep the color from scoreBand and override the label.
  const band = useMemo(() => {
    const local = scoreBand(healthScore);
    if (report?.health_band) return { ...local, label: report.health_band };
    return local;
  }, [healthScore, report?.health_band]);

  const loadAudio = async (langOverride) => {
    const l = langOverride || lang;
    try {
      const res = await fetch(`${API_BASE}/reports/${id}/audio?lang=${l}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("efgh_token")}` },
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const u = URL.createObjectURL(blob);
      setAudioUrl(u);
      return u;
    } catch { toast.error("Audio unavailable"); return null; }
  };

  // Localised summary — refetched every time the user picks a different language.
  const [localizedSummary, setLocalizedSummary] = useState(null);
  const loadTranslation = async (langOverride) => {
    const l = langOverride || lang;
    if (l === "en") { setLocalizedSummary(null); return; }
    try {
      const res = await fetch(`${API_BASE}/reports/${id}/translated-summary?lang=${l}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("efgh_token")}` },
      });
      if (!res.ok) throw new Error();
      const d = await res.json();
      setLocalizedSummary(d.text || null);
    } catch { setLocalizedSummary(null); }
  };

  const switchLang = async (l) => {
    if (l === lang) return;
    setLang(l);
    if (audioRef.current) { audioRef.current.pause(); setPlaying(false); }
    setAudioUrl(null);
    await Promise.all([loadAudio(l), loadTranslation(l)]);
  };

  const togglePlay = async () => {
    if (!audioUrl) await loadAudio();
    if (!audioRef.current) return;
    if (playing) { audioRef.current.pause(); setPlaying(false); }
    else {
      try { await audioRef.current.play(); setPlaying(true); }
      catch { setPlaying(false); }
    }
  };

  if (loading) {
    return <div className="min-h-screen grid place-items-center"><Loader2 className="w-8 h-8 animate-spin text-efgh-green" /></div>;
  }
  if (!report) {
    return <div className="min-h-screen bg-white"><TopNav /><div className="p-8 text-center text-ink/60">Report not found.</div></div>;
  }

  return (
    <div className="min-h-screen bg-[#F7F8FA]">
      <TopNav />

      {/* Meta strip */}
      <div className="border-b border-border bg-white print:hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-ink/50 font-heading font-semibold">Smart Report</div>
            <h1 className="mt-0.5 font-heading font-bold text-2xl sm:text-3xl text-ink" data-testid="report-title">{report.package_name || "Diagnostic Report"}</h1>
            <div className="text-sm text-ink/60 mt-1">
              {report.patient_name}{report.patient_age ? ` · ${report.patient_age}y` : ""}{report.report_date ? ` · ${report.report_date}` : ""}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`pill ${(report.status === "ready" || report.status === "published") ? "pill-normal" : report.status === "pending_review" ? "pill-borderline" : report.status === "processing" ? "pill-borderline" : report.status === "failed" ? "pill-high" : "pill-low"}`} data-testid="report-status">
              {report.status === "pending_review" ? "Pending lab review" : report.status === "published" ? "Published" : report.status}
            </span>
            {report.reviewer_name && report.reviewed_at && (
              <span className="pill pill-normal text-[11px]" data-testid="reviewer-badge" title={`Reviewed by ${report.reviewer_name}`}>
                <Check className="w-3 h-3" /> Verified · {report.reviewer_name}
              </span>
            )}
            {(report.status === "ready" || report.status === "published") && (
              <>
                <Button data-testid="share-btn" onClick={() => setShareOpen(true)} variant="outline" className="border-ink text-ink hover:bg-muted h-9">
                  <Share2 className="w-4 h-4 mr-1.5" /> Share
                </Button>
                <Button data-testid="pdf-btn" onClick={handlePrint} disabled={pdfBusy} variant="outline" title={coverPageEnabled ? "Download PDF (includes lab cover page as page 1) — server-rendered for pixel-perfect quality" : "Download PDF — server-rendered vector output with selectable text"} className="border-ink text-ink hover:bg-muted h-9">
                  {pdfBusy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Download className="w-4 h-4 mr-1.5" />} PDF
                </Button>
              </>
            )}
            {report.status === "pending_review" && (
              <Link to={`/lab/reports/${id}/review`} data-testid="review-cta" className="pill bg-efgh-yellow text-ink text-[11px] hover:opacity-90">
                <ClipboardCheck className="w-3 h-3" /> Review & publish
              </Link>
            )}
            {listenEnabled && (report.status === "ready" || report.status === "published") && (
              <div className="flex items-center gap-2">
                <div className="flex rounded-lg border border-border p-0.5 bg-white" data-testid="audio-lang-switch">
                  <button data-testid="audio-lang-en" onClick={() => switchLang("en")} className={`text-xs px-2.5 py-1 rounded-md ${lang === "en" ? "bg-ink text-white" : "text-ink/60"}`}>EN</button>
                  {regionalLang && regionalLang !== "en" && (
                    <button data-testid={`audio-lang-${regionalLang}`} onClick={() => switchLang(regionalLang)} className={`text-xs px-2.5 py-1 rounded-md ${lang === regionalLang ? "bg-ink text-white" : "text-ink/60"}`}>{LANG_LABELS_NATIVE[regionalLang] || regionalLang.toUpperCase()}</button>
                  )}
                </div>
                <Button data-testid="audio-play" onClick={togglePlay} variant="outline" className="border-ink text-ink hover:bg-muted h-9">
                  {playing ? <Pause className="w-4 h-4 mr-1.5" /> : <Play className="w-4 h-4 mr-1.5" />} Listen
                </Button>
                <audio ref={audioRef} src={audioUrl || undefined} onEnded={() => setPlaying(false)} className="hidden" />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Loading states for pending/processing/pending_review */}
      {(report.status === "processing" || report.status === "pending") ? (
        <div className="max-w-3xl mx-auto p-8">
          <Card className="p-10 rounded-2xl text-center">
            <Loader2 className="w-10 h-10 mx-auto animate-spin text-efgh-green" />
            <div className="mt-4 font-heading font-semibold text-ink">Our AI is reading your report…</div>
            <div className="text-sm text-ink/60 mt-1">This usually takes 30–60 seconds.</div>
          </Card>
        </div>
      ) : (report.status === "pending_review" && !smart) ? (
        <div className="max-w-3xl mx-auto p-8">
          <Card className="p-10 rounded-2xl text-center" data-testid="under-review">
            <ClipboardCheck className="w-10 h-10 mx-auto text-efgh-yellow" />
            <div className="mt-4 font-heading font-semibold text-ink">Your report is with the lab reviewer</div>
            <div className="text-sm text-ink/60 mt-1 max-w-md mx-auto">Our AI has parsed your report. A qualified lab reviewer is now checking every value before we send it your way. This usually takes an hour or two.</div>
          </Card>
        </div>
      ) : report.status === "failed" ? (
        <div className="max-w-3xl mx-auto p-8">
          <Card className="p-8 rounded-2xl text-center border-danger/40">
            <AlertCircle className="w-10 h-10 mx-auto text-danger" />
            <div className="mt-3 font-heading font-semibold text-ink">Could not process this report</div>
            <div className="text-sm text-ink/60 mt-1">Please re-upload a clearer image or PDF.</div>
          </Card>
        </div>
      ) : smart ? (
        <div
          className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 print:hidden overflow-x-hidden"
          data-testid="report-canvas"
        >
          {/* Lab letterhead at top (on-screen only). Print output is fully controlled by <PrintDocument>. */}
          {lab && lab.letterhead_mode !== "custom" && (
            <div className="-mx-4 sm:-mx-6 lg:-mx-8 mb-4" data-testid="lab-letterhead-onscreen">
              <LabLetterhead lab={lab} report={report} />
            </div>
          )}
          {/* Patient details table — replaces the empty-space Health-Score hero */}
          <PatientMetaCard report={report} smart={smart} healthScore={healthScore} band={band} onAskAI={() => setChatOpen(true)} />
          {/* Layout: sticky section-nav on the left + content on the right. */}
          <div className="grid lg:grid-cols-[200px_1fr] gap-6">
            <SectionNav smart={smart} history={history} />

            {/* Single-scroll on-screen report — every section stacked */}
            <div className="space-y-6 min-w-0" data-testid="report-sections">
              <ReportSection title="Smart Health Summary" testid="section-summary">
                <SummaryTab smart={smart} healthScore={healthScore} overrideSummaryText={localizedSummary} />
              </ReportSection>
              {(smart.biomarkers || []).length > 0 && (smart.health_snapshot || []).some((h) => h.status !== "not_tested") && (
                <ReportSection title="Overall Health Snapshot" testid="section-bodymap">
                  <InteractiveBodyMap
                    smart={smart}
                    history={history}
                    onJumpToBiomarkers={({ label, items }) => {
                      // Filter the biomarker table down to just this system's items,
                      // then smooth-scroll into view.
                      setBioFilter({ label, names: new Set((items || []).map((b) => b.name)) });
                      const el = document.querySelector('[data-testid="section-biomarkers"]');
                      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                    }}
                  />
                </ReportSection>
              )}
              {(smart.biomarkers || []).length > 0 && (
                <ReportSection title="Biomarker Details" testid="section-biomarkers">
                  <BiomarkersTab smart={smart} onSelect={setSelectedBio} history={history} systemFilter={bioFilter} onClearSystemFilter={() => setBioFilter(null)} />
                </ReportSection>
              )}
              {(smart.critical_findings || []).length > 0 && (
                <ReportSection title="Critical Findings" testid="section-critical">
                  <CriticalTab smart={smart} onSelect={setSelectedBio} history={history} />
                </ReportSection>
              )}
              {history.length > 0 && (
                <ReportSection title="Compare to Past" testid="section-trends">
                  <TrendsTab smart={smart} history={history} />
                </ReportSection>
              )}
              {(smart.diet_recommendations?.length || smart.physical_activity?.length || smart.avoid_watch_out?.length) > 0 && (
                <ReportSection title="Lifestyle &amp; Food" testid="section-lifestyle">
                  <LifestyleTab smart={smart} />
                </ReportSection>
              )}
              {(smart.follow_up_tests || []).length > 0 && (
                <ReportSection title="Book Follow-Up Tests" testid="section-followup">
                  <FollowUpTab smart={smart} report={report} lab={lab} />
                </ReportSection>
              )}
              <ReportIntegrityFooter smart={smart} />
            </div>
          </div>{/* /section-nav grid */}

          <p className="mt-10 text-xs text-ink/50 text-center max-w-2xl mx-auto">
            <b>Medical Disclaimer:</b> This report is generated by EFGH&apos;s AI for educational purposes only. Always consult a qualified healthcare professional before making any health decisions.
          </p>

          {/* Digital bottom letterhead — on-screen only. */}
          {lab && lab.letterhead_mode !== "custom" && (
            <div className="-mx-4 sm:-mx-6 lg:-mx-8 mt-6" data-testid="lab-letterhead-bottom-onscreen">
              <LabLetterheadBottom lab={lab} report={report} />
            </div>
          )}

          {/* ═══════════════════════════════════════════════════════════════════════
              DETERMINISTIC PDF ENGINE — off-screen, deep-linked via `printRef`.
              react-to-print clones this element into a hidden iframe when the user
              clicks Download PDF, producing pixel-accurate paginated output that
              respects each lab's configured header/footer heights + 10 mm safe pads.
              ═══════════════════════════════════════════════════════════════════════ */}
          <PrintDocument
            ref={printRef}
            report={report}
            lab={lab}
            smart={smart}
            healthScore={healthScore}
            band={band}
            history={history}
          />
        </div>
      ) : null}

      {/* Biomarker drill-down */}
      <BiomarkerModal bio={selectedBio} history={history} onClose={() => setSelectedBio(null)} />
      {/* Ask AI drawer */}
      <AskAIDrawer open={chatOpen} onClose={() => setChatOpen(false)} reportId={id} />
      {/* Share dialog */}
      <ShareDialog open={shareOpen} onClose={() => setShareOpen(false)} reportId={id} />
      {/* Review wizard (emoji rating, redirects to external for high ratings) */}
      {(report.status === "ready" || report.status === "published") && (
        <ReviewWizard reportId={id} />
      )}
    </div>
  );
}

/* ============ SHARE DIALOG ============ */
function ShareDialog({ open, onClose, reportId }) {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get(`/reports/${reportId}/share`).then((r) => setInfo(r.data)).catch(() => toast.error("Could not create share link")).finally(() => setLoading(false));
  }, [open, reportId]);

  const shareUrl = info ? `${window.location.origin}${info.path}` : "";
  const doCopy = () => { navigator.clipboard.writeText(shareUrl); setCopied(true); setTimeout(() => setCopied(false), 1800); };
  const revoke = async () => {
    if (!window.confirm("Revoke the current share link? Anyone with the old link will lose access.")) return;
    try { await api.delete(`/reports/${reportId}/share`); toast.success("Link revoked"); onClose(); }
    catch { toast.error("Revoke failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="share-dialog">
        <DialogTitle className="font-heading">Share this Smart Report</DialogTitle>
        <p className="text-sm text-ink/60">Anyone with this link can view a read-only, lab-branded version of the report — no login required.</p>
        {loading || !info ? (
          <div className="py-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-ink/40" /></div>
        ) : (
          <div className="space-y-4">
            <div className="p-3 rounded-lg border border-border bg-muted/30 flex items-center gap-2">
              <code className="text-sm text-ink/85 flex-1 truncate" data-testid="share-url">{shareUrl}</code>
              <Button size="sm" onClick={doCopy} data-testid="share-copy" className="bg-ink hover:bg-ink/90 text-white h-9">
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
            <div className="flex gap-2 flex-wrap">
              <a target="_blank" rel="noreferrer" href={`https://wa.me/?text=${encodeURIComponent("View my Smart Report: " + shareUrl)}`} className="pill bg-[#25D366] text-white">WhatsApp</a>
              <a target="_blank" rel="noreferrer" href={`mailto:?subject=${encodeURIComponent("My Smart Report")}&body=${encodeURIComponent(shareUrl)}`} className="pill bg-ink text-white">Email</a>
            </div>
            <div className="pt-2 border-t border-border flex items-center justify-between">
              <div className="text-xs text-ink/50">This link can be revoked at any time.</div>
              <button onClick={revoke} data-testid="share-revoke" className="text-xs text-danger font-medium hover:underline">Revoke link</button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ============ LEFT RAIL: HEALTH SCORE ============ */
function HealthScoreCard({ score, band, counts }) {
  const pct = score ?? 0;
  const circumference = 2 * Math.PI * 42;
  const dash = (pct / 100) * circumference;
  return (
    <Card className="rounded-2xl p-5 text-center" data-testid="health-score-card">
      <div className="text-[11px] uppercase tracking-[0.22em] text-ink/50 font-heading font-semibold">Health Score</div>
      <div className="relative mt-3 mx-auto w-[120px] h-[120px]">
        <svg viewBox="0 0 100 100" className="-rotate-90 w-full h-full">
          <circle cx="50" cy="50" r="42" strokeWidth="8" stroke="#EEF0F4" fill="none" />
          <circle cx="50" cy="50" r="42" strokeWidth="8" stroke={band.color} fill="none"
            strokeLinecap="round" strokeDasharray={`${dash} ${circumference}`} />
        </svg>
        <div className="absolute inset-0 grid place-items-center">
          <div>
            <div className="font-heading font-bold text-3xl text-ink leading-none" data-testid="health-score-value">{score ?? "—"}</div>
            <div className="text-[10px] uppercase tracking-widest text-ink/50 mt-1">/ 100</div>
          </div>
        </div>
      </div>
      <div className="mt-3 font-heading font-semibold text-ink" style={{ color: band.color }}>{band.label}</div>
      <div className="mt-4 grid grid-cols-3 gap-1.5 text-center">
        <div className="rounded-lg bg-danger-light py-2">
          <div className="font-heading font-bold text-danger">{counts.need}</div>
          <div className="text-[10px] uppercase tracking-wider text-ink/60">Attn</div>
        </div>
        <div className="rounded-lg bg-warning-light py-2">
          <div className="font-heading font-bold text-warning">{counts.bord}</div>
          <div className="text-[10px] uppercase tracking-wider text-ink/60">Watch</div>
        </div>
        <div className="rounded-lg bg-success-light py-2">
          <div className="font-heading font-bold text-success">{counts.norm}</div>
          <div className="text-[10px] uppercase tracking-wider text-ink/60">OK</div>
        </div>
      </div>
    </Card>
  );
}

/* ============ TABS ============ */
export function SummaryTab({ smart, healthScore, overrideSummaryText }) {
  // When the body diagram will render (biomarkers present), it already
  // conveys "Need attention / Borderline / All clear" via coloured hotspots
  // AND covers the system-by-system snapshot visually — so we hide those two
  // duplicative cards from the digital report but keep them for the PDF
  // (the PDF's PrintDocument has its own copies that always render).
  const hasBodyDiagram = (smart.biomarkers || []).length > 0;
  const displaySummary = overrideSummaryText || smart.ai_summary;
  // Culture rows are shown in a dedicated card (with antibiogram table) instead
  // of the plain biomarker table — collect them here so we can render + tell
  // the biomarker table to skip them below.
  const cultures = (smart.biomarkers || []).filter((b) => b.panel === "Culture & Sensitivity");
  // Filter out `not_tested` snapshot chips so the section stays informative.
  // If every organ is untested, hide the section entirely.
  const testedSnapshot = (smart.health_snapshot || []).filter((h) => h.status !== "not_tested");
  return (
    <div className="space-y-6">
      <Card className="p-6 rounded-2xl">
        <div className="flex items-center gap-2 text-efgh-green font-heading font-semibold"><Sparkles className="w-4 h-4" /> Smart Health Summary</div>
        <p className="mt-4 text-ink/80 leading-relaxed whitespace-pre-line" data-testid="ai-summary-text">{displaySummary}</p>
        {!hasBodyDiagram && (
          <div className="mt-6 grid grid-cols-3 gap-2 sm:gap-3" data-testid="summary-stat-mini">
            <StatMini bg="bg-danger-light" fg="text-danger" val={smart.parameters_needing_attention} label="Need attention" icon="⚠️" />
            <StatMini bg="bg-warning-light" fg="text-warning" val={smart.parameters_borderline} label="Borderline" icon="🔍" />
            <StatMini bg="bg-success-light" fg="text-success" val={smart.parameters_normal} label="All clear" icon="✓" />
          </div>
        )}
      </Card>

      {cultures.length > 0 && <CultureCard cultures={cultures} />}

      {(smart.general_notes || []).length > 0 && (
        <Card className="p-6 rounded-2xl border-l-4 border-efgh-orange" data-testid="cotesting-notes">
          <div className="font-heading font-semibold text-ink text-lg">Clinical notes</div>
          <ul className="mt-3 space-y-2 text-sm text-ink/80">
            {smart.general_notes.map((n, i) => (
              <li key={i} className="flex gap-2"><span className="text-efgh-orange">▸</span><span>{n}</span></li>
            ))}
          </ul>
        </Card>
      )}

      {!hasBodyDiagram && testedSnapshot.length > 0 && (
        <Card className="p-6 rounded-2xl" data-testid="summary-health-snapshot">
          <div className="font-heading font-semibold text-ink text-lg">Health Snapshot</div>
          <p className="text-sm text-ink/60 mt-1">System-by-system overview from your report</p>
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
            {testedSnapshot.map((h) => (
              <div key={h.name} data-testid={`snapshot-${h.name}`} className="p-4 rounded-xl border border-border text-center card-hover">
                <div className="text-3xl">{iconMap[h.name] || "🩺"}</div>
                <div className="mt-2 text-sm font-medium text-ink">{h.name}</div>
                <span className={`pill pill-${h.status} mt-1.5 text-[10px]`}>{h.status.replace("_", " ")}</span>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

/* ============ CULTURE CARD ============
   Dedicated renderer for microbiology results — organism(s), CFU, and the
   antibiogram as a colour-coded S/I/R chip grid. */
function CultureCard({ cultures }) {
  const sirColor = (r) => ({
    S: "bg-success-light text-success border-success/30",
    I: "bg-warning-light text-warning border-warning/30",
    R: "bg-danger-light text-danger border-danger/30",
  }[r] || "bg-muted text-ink border-border");
  const sirLabel = (r) => ({ S: "Sensitive", I: "Intermediate", R: "Resistant" }[r] || r);
  return (
    <Card className="p-6 rounded-2xl" data-testid="culture-card">
      <div className="flex items-center gap-2 text-efgh-blue font-heading font-semibold text-lg">
        <span>🧫</span> Culture & Sensitivity
      </div>
      <div className="mt-4 space-y-6">
        {cultures.map((c, ci) => {
          const isPositive = (c.organism || (c.organisms && c.organisms.length > 0));
          const organisms = c.organisms || (c.organism ? [c.organism] : []);
          return (
            <div key={c.code || ci} className="rounded-xl border border-border overflow-hidden" data-testid={`culture-row-${c.code || ci}`}>
              <div className={`px-4 py-3 ${isPositive ? "bg-danger-light/50" : "bg-success-light/50"} border-b border-border`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <div className="text-xs uppercase tracking-wider text-ink/50">{c.name}</div>
                    <div className="mt-1 font-heading font-semibold text-ink text-base">
                      {isPositive ? "Growth detected" : "No growth"}
                    </div>
                    {organisms.length > 0 && (
                      <div className="mt-1 text-sm text-ink" data-testid={`culture-organisms-${c.code || ci}`}>
                        <span className="text-ink/50">Organism{organisms.length > 1 ? "s" : ""}:</span>{" "}
                        {organisms.map((o, i) => (
                          <span key={i} className="italic font-medium">{o}{i < organisms.length - 1 ? ", " : ""}</span>
                        ))}
                      </div>
                    )}
                    {c.cfu && <div className="text-xs text-ink/60 mt-0.5">CFU count: <span className="font-medium">{c.cfu}</span></div>}
                  </div>
                  <span className={`pill ${isPositive ? "pill-concern" : "pill-normal"} text-[11px]`}>{c.status}</span>
                </div>
                {c.interpretation && (
                  <p className="mt-2 text-xs text-ink/70">{c.interpretation}</p>
                )}
              </div>

              {(c.antibiogram || []).length > 0 && (
                <div className="p-4">
                  <div className="text-xs uppercase tracking-wider text-ink/50 mb-2">Antibiogram (S / I / R)</div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2" data-testid={`antibiogram-${c.code || ci}`}>
                    {c.antibiogram.map((ab, ai) => (
                      <div key={ai} className={`flex items-center justify-between gap-2 px-3 py-2 rounded-lg border text-sm ${sirColor(ab.result)}`}>
                        <span className="font-medium truncate" title={ab.antibiotic}>{ab.antibiotic}</span>
                        <span className="text-[10px] uppercase tracking-wider font-bold shrink-0" title={sirLabel(ab.result)}>{ab.result}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 flex items-center gap-3 text-[10px] text-ink/50">
                    <span><span className="inline-block w-2 h-2 rounded-full bg-success align-middle mr-1" />S = Sensitive</span>
                    <span><span className="inline-block w-2 h-2 rounded-full bg-warning align-middle mr-1" />I = Intermediate</span>
                    <span><span className="inline-block w-2 h-2 rounded-full bg-danger align-middle mr-1" />R = Resistant</span>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
}

export function BiomarkersTab({ smart, onSelect, systemFilter, onClearSystemFilter }) {
  const [q, setQ] = useState("");
  // Culture rows have their own dedicated card in the SummaryTab — exclude them
  // from the plain biomarker table to avoid duplicating information.
  const all = (smart.biomarkers || []).filter((b) => b.panel !== "Culture & Sensitivity");
  // System filter applies first (from body-map jump). Free-text search narrows further.
  const systemFiltered = systemFilter?.names
    ? all.filter((b) => systemFilter.names.has(b.name))
    : all;
  const list = systemFiltered.filter((b) =>
    !q || `${b.name} ${b.category || ""} ${b.panel || ""} ${b.method || ""}`.toLowerCase().includes(q.toLowerCase())
  );
  // Group by panel (fallback to category or "Other")
  const groups = React.useMemo(() => {
    const map = new Map();
    list.forEach((b) => {
      const panel = b.panel || b.category || "Other";
      if (!map.has(panel)) map.set(panel, []);
      map.get(panel).push(b);
    });
    // Sort panels by number of abnormal values (worst-first)
    return Array.from(map.entries()).sort(([, a], [, b]) => {
      const abnA = a.filter((x) => x.status !== "normal").length;
      const abnB = b.filter((x) => x.status !== "normal").length;
      if (abnA !== abnB) return abnB - abnA;
      return b.length - a.length;
    });
  }, [list]);

  // Panel-level lab comments (from Zero Data Loss extraction).
  const panelComments = React.useMemo(() => {
    const map = new Map();
    (smart.panel_comments || []).forEach((pc) => {
      if (pc?.panel && pc?.comment) map.set(pc.panel, pc.comment);
    });
    return map;
  }, [smart.panel_comments]);

  return (
    <Card className="p-6 rounded-2xl">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="font-heading font-semibold text-ink text-lg">Biomarker Details · All {(smart.biomarkers || []).length} parameters</div>
          <p className="text-sm text-ink/60">Every laboratory row from your original report — grouped by panel. Click any row for details.</p>
        </div>
        <Input data-testid="biomarker-search" placeholder="Search biomarker, panel or method…" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-[280px] h-10" />
      </div>
      {systemFilter?.label && (
        <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="biomarker-filter-chip">
          <span className="text-xs text-ink/50">Filtered by:</span>
          <span className="inline-flex items-center gap-1.5 pill bg-ink text-white text-[11px]">
            {systemFilter.label} system
            <button type="button" onClick={() => onClearSystemFilter && onClearSystemFilter()} className="hover:opacity-70" aria-label="Clear system filter" data-testid="biomarker-filter-clear">✕</button>
          </span>
          <span className="text-xs text-ink/50">· Showing {list.length} of {all.length}</span>
        </div>
      )}
      <div className="mt-5 space-y-6">
        {groups.map(([panel, items]) => {
          const abnormal = items.filter((x) => x.status !== "normal").length;
          const comment = panelComments.get(panel);
          return (
            <div key={panel} data-testid={`panel-${panel.replace(/\s+/g, "-")}`}>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-1 h-6 rounded-full bg-efgh-orange" />
                <div className="font-heading font-semibold text-ink text-base">{panel}</div>
                <span className="text-[11px] uppercase tracking-wider text-ink/40">{items.length} tests</span>
                {abnormal > 0 && <span className="pill pill-borderline text-[10px]">{abnormal} flagged</span>}
              </div>
              <div className="divide-y divide-border rounded-xl border border-border overflow-hidden">
                {items.map((b, i) => (
                  <button
                    key={`${panel}-${i}`}
                    data-testid={`bio-row-${panel.replace(/\s+/g, "-")}-${i}`}
                    onClick={() => onSelect(b)}
                    className="w-full text-left p-3 sm:p-4 hover:bg-muted transition-all"
                  >
                    {/* Mobile / narrow layout: stacked. Desktop (≥md): single row. */}
                    <div className="flex items-start gap-3">
                      <span className="w-2.5 h-2.5 rounded-full shrink-0 mt-2" style={{ backgroundColor: statusColor(b.status) }} title={b.status} />
                      <div className="flex-1 min-w-0">
                        {/* Top row: name + status pill + chevron always aligned right */}
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-ink truncate flex-1 min-w-0" title={b.name}>{b.name}</span>
                          {b.flag && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-danger-light text-danger shrink-0">{b.flag}</span>}
                          <span className={`pill pill-${b.status} shrink-0 text-[10px]`}>{String(b.status).toUpperCase()}</span>
                          <ChevronRight className="w-4 h-4 text-ink/40 shrink-0" />
                        </div>
                        {/* Value + reference range — big & readable, own line */}
                        <div className="mt-1 flex items-baseline flex-wrap gap-x-2 gap-y-0.5">
                          <span className="font-heading font-bold text-ink text-lg leading-none">
                            {b.value}<span className="text-xs text-ink/50 font-normal ml-1">{b.unit}</span>
                          </span>
                          {(b.reference_range || b.ref_range) && (
                            <span className="text-[11px] text-ink/50">Ref: {b.reference_range || b.ref_range}</span>
                          )}
                        </div>
                        {(b.interpretation || b.description) && (
                          <div className="text-xs text-ink/60 mt-1.5 leading-snug">{b.interpretation || b.description}</div>
                        )}
                        {(b.method || b.remarks || b.lab_comment || b.note) && (
                          <div className="mt-1.5 text-[11px] text-ink/50 space-y-0.5">
                            {b.method && <div><span className="text-ink/40 uppercase tracking-wider">Method:</span> {b.method}</div>}
                            {b.remarks && <div><span className="text-ink/40 uppercase tracking-wider">Remarks:</span> {b.remarks}</div>}
                            {(b.lab_comment || b.note) && <div><span className="text-ink/40 uppercase tracking-wider">Note:</span> {b.lab_comment || b.note}</div>}
                          </div>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
              {comment && (
                <div data-testid={`panel-comment-${panel.replace(/\s+/g, "-")}`} className="mt-2 p-3 rounded-lg bg-muted/40 border-l-2 border-efgh-green text-[12px] text-ink/75 italic">
                  <b className="not-italic text-ink/60 mr-1">Laboratory note:</b>{comment}
                </div>
              )}
            </div>
          );
        })}
        {list.length === 0 && <div className="p-8 text-center text-ink/50 text-sm">No biomarkers match your search.</div>}
      </div>
      {(smart.general_notes || []).length > 0 && (
        <div className="mt-6 pt-4 border-t border-border" data-testid="general-notes">
          <div className="text-[11px] uppercase tracking-widest text-ink/50 font-heading font-semibold mb-2">Laboratory Notes &amp; Observations</div>
          <ul className="space-y-1.5">
            {smart.general_notes.map((n, i) => (
              <li key={i} className="text-[12px] text-ink/70 leading-relaxed pl-4 relative">
                <span className="absolute left-0 top-1.5 w-1 h-1 rounded-full bg-ink/40" />{n}
              </li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  );
}

export function CriticalTab({ smart, onSelect }) {
  const crit = smart.critical_findings || [];
  if (crit.length === 0) {
    return <Card className="p-10 rounded-2xl text-center"><div className="text-4xl">✅</div><div className="mt-3 font-heading font-semibold text-ink">No critical findings</div><p className="text-sm text-ink/60 mt-1">All your values are within safe ranges.</p></Card>;
  }
  return (
    <Card className="p-6 rounded-2xl border-danger/30">
      <div className="font-heading font-semibold text-danger text-lg flex items-center gap-2"><ShieldAlert className="w-5 h-5" /> Critical Findings</div>
      <p className="text-sm text-ink/60 mt-1">Parameters outside normal range — with what it means and what to do.</p>
      <div className="mt-4 space-y-4">
        {crit.map((b, i) => {
          const range = parseRange(b.reference_range);
          const val = parseFloat(b.value);
          const pos = rangePosition(val, range);
          return (
            <div key={i} data-testid={`critical-${i}`} className="p-5 rounded-xl bg-danger-light/60 border border-danger/20">
              <div className="grid md:grid-cols-[1fr_auto] gap-4">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: statusColor(b.status) }} />
                    <div className="font-heading font-semibold text-ink">{b.name}</div>
                    <span className={`pill pill-${b.status}`}>{String(b.status).toUpperCase()}</span>
                  </div>
                  {b.interpretation && (
                    <div className="mt-2 text-sm text-ink/80"><b className="text-danger">Impact:</b> {b.interpretation}</div>
                  )}
                  <div className="mt-2 text-sm text-ink/80"><b className="text-efgh-green">How to improve:</b> Consult your doctor, adjust diet & lifestyle, retest in 4-6 weeks.</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-heading font-bold text-danger text-2xl">{b.value} <span className="text-xs font-normal text-ink/50">{b.unit}</span></div>
                  <div className="text-xs text-ink/50">Normal: {b.reference_range}</div>
                </div>
              </div>
              {range && pos !== null && <RangeBar range={range} value={val} unit={b.unit} status={b.status} pos={pos} />}
              <button onClick={() => onSelect(b)} className="mt-3 text-xs font-medium text-ink/70 hover:text-ink underline underline-offset-2">See trend & details →</button>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function RangeBar({ range, value, unit, status, pos }) {
  const span = range.max - range.min;
  const visStart = range.min - span * 0.35;
  const visEnd = range.max + span * 0.35;
  const normalStartPct = ((range.min - visStart) / (visEnd - visStart)) * 100;
  const normalEndPct = ((range.max - visStart) / (visEnd - visStart)) * 100;
  return (
    <div className="mt-4">
      <div className="relative h-2.5 rounded-full bg-gradient-to-r from-efgh-blue/25 via-efgh-green/25 to-danger/25 overflow-visible">
        <div className="absolute top-0 h-2.5 bg-efgh-green/40" style={{ left: `${normalStartPct}%`, width: `${Math.max(0, normalEndPct - normalStartPct)}%` }} />
        <div className="absolute -top-1 w-1 h-4 bg-white border border-ink/30" style={{ left: `${normalStartPct}%` }} title={`Min ${range.min}`} />
        <div className="absolute -top-1 w-1 h-4 bg-white border border-ink/30" style={{ left: `${normalEndPct}%` }} title={`Max ${range.max}`} />
        <div className="absolute -top-2 -translate-x-1/2 flex flex-col items-center" style={{ left: `${pos}%` }}>
          <div className="w-4 h-4 rounded-full border-2 border-white shadow-md" style={{ backgroundColor: statusColor(status) }} />
        </div>
      </div>
      <div className="mt-1.5 flex justify-between text-[10px] text-ink/50 font-mono">
        <span>{visStart.toFixed(1)}</span>
        <span className="text-efgh-green">{range.min}{unit ? ` ${unit}` : ""}</span>
        <span className="text-efgh-green">{range.max}</span>
        <span>{visEnd.toFixed(1)}</span>
      </div>
    </div>
  );
}

function TrendsTab({ smart, history }) {
  const current = smart.biomarkers || [];
  const withHistory = current.filter((b) => history.some((r) => r.biomarkers?.some((x) => x.name === b.name))).slice(0, 6);

  if (history.length < 2) {
    return (
      <Card className="p-8 rounded-2xl text-center bg-gradient-to-br from-slate-50 to-white" data-testid="trends-empty">
        <div className="w-16 h-16 mx-auto rounded-2xl bg-white shadow-sm grid place-items-center">
          <TrendingUp className="w-7 h-7 text-efgh-blue" />
        </div>
        <div className="mt-4 font-heading font-semibold text-ink text-lg">Trends unlock on your 2nd report</div>
        <p className="text-sm text-ink/60 mt-1.5 max-w-md mx-auto">Upload one more lab report and we&apos;ll plot how every overlapping biomarker has changed over time — with the normal reference range highlighted in green on every chart.</p>
        <div className="mt-5">
          <Link to="/patient/upload">
            <Button data-testid="trends-upload-cta" className="bg-ink text-white hover:bg-ink/90"><Upload className="w-4 h-4 mr-2" /> Upload another report</Button>
          </Link>
        </div>
      </Card>
    );
  }
  if (withHistory.length === 0) {
    return (
      <Card className="p-10 rounded-2xl text-center">
        <div className="mt-3 font-heading font-semibold text-ink">No overlapping biomarkers</div>
        <p className="text-sm text-ink/60 mt-1">None of the current biomarkers appear in your previous reports.</p>
      </Card>
    );
  }
  return (
    <div className="space-y-4">
      <div className="text-sm text-ink/60">Trend across your last {history.length} reports.</div>
      <div className="grid md:grid-cols-2 gap-4">
        {withHistory.map((b) => <TrendChart key={b.name} biomarker={b} history={history} />)}
      </div>
    </div>
  );
}

export function TrendChart({ biomarker, history }) {
  const data = history.map((r) => {
    const match = (r.biomarkers || []).find((x) => x.name === biomarker.name);
    const v = match ? parseFloat(match.value) : null;
    return { date: (r.report_date || r.created_at || "").slice(0, 10), value: isNaN(v) ? null : v };
  }).filter((d) => d.value !== null);
  const values = data.map((d) => d.value);
  // Parse reference range once so we can shade the "normal" zone on the chart.
  // NOTE: `parseRange` returns `{min, max}` — earlier this code read `.low/.high`
  // which was always undefined, so the normal zone never rendered and outlier
  // values dragged the axis to unhelpful ranges (e.g. showing "0.0001" ticks).
  const refRange = parseRange(biomarker.reference_range);
  const rangeLo = Number.isFinite(refRange?.min) ? refRange.min : null;
  const rangeHi = Number.isFinite(refRange?.max) ? refRange.max : null;
  // Filter out obviously-noisy values (e.g. OCR misreads producing 0.0001) so the
  // Y-axis stays anchored to the clinically meaningful range around the reference.
  const cleanValues = values.filter((v) => v > 0 && Number.isFinite(v));
  const domainMin = Math.min(...cleanValues, rangeLo ?? Infinity);
  const domainMax = Math.max(...cleanValues, rangeHi ?? -Infinity);
  const pad = Math.max((domainMax - domainMin) * 0.2, Math.abs(domainMax) * 0.1, 1);
  // Directional arrow — first vs last point.
  const first = values[0], last = values[values.length - 1];
  const delta = last - first;
  const pct = first ? ((delta / first) * 100).toFixed(0) : 0;
  const trendUp = delta > 0;
  return (
    <Card className="p-5 rounded-2xl" data-testid={`trend-${biomarker.name}`}>
      <div className="flex items-center justify-between">
        <div className="min-w-0">
          <div className="font-medium text-ink truncate">{biomarker.name}</div>
          <div className="text-xs text-ink/50">{biomarker.category}</div>
        </div>
        <div className="text-right shrink-0">
          <div className="font-heading font-bold text-ink text-lg">{biomarker.value}<span className="text-xs text-ink/50 font-normal ml-1">{biomarker.unit}</span></div>
          <div className="text-[11px] flex items-center justify-end gap-1">
            <TrendingUp className={`w-3 h-3 ${trendUp ? "text-danger rotate-0" : "text-success rotate-180"}`} />
            <span className={trendUp ? "text-danger" : "text-success"}>{delta > 0 ? "+" : ""}{pct}%</span>
          </div>
        </div>
      </div>
      <div className="mt-3 h-32">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="#EEF0F4" />
            <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
            <YAxis domain={[domainMin - pad, domainMax + pad]} tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} width={30} />
            {rangeLo !== null && rangeHi !== null && (
              <ReferenceArea y1={rangeLo} y2={rangeHi} fill="#DCFCE7" fillOpacity={0.6} stroke="#86EFAC" strokeDasharray="2 2" label={{ value: "Normal", position: "insideTopRight", fontSize: 9, fill: "#16A34A" }} />
            )}
            <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12, border: "1px solid #E5E7EB" }} />
            <Line type="monotone" dataKey="value" stroke={statusColor(biomarker.status)} strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>
      {(rangeLo !== null || rangeHi !== null) && (
        <div className="mt-2 text-[10.5px] text-ink/50 flex items-center justify-between">
          <span>Range {rangeLo ?? "—"}–{rangeHi ?? "—"} {biomarker.unit}</span>
          <span>{data.length} reports</span>
        </div>
      )}
    </Card>
  );
}

export function LifestyleTab({ smart }) {
  return (
    <div className="grid md:grid-cols-3 gap-4">
      <Advice title="Diet Recommendations" icon={Utensils} color="text-success" bg="bg-success-light" items={smart.diet_recommendations} />
      <Advice title="Physical Activity" icon={Activity} color="text-info" bg="bg-info-light" items={smart.physical_activity} />
      <Advice title="Avoid / Watch Out" icon={ShieldAlert} color="text-danger" bg="bg-danger-light" items={smart.avoid_watch_out} />
    </div>
  );
}

export function FollowUpTab({ smart, report, lab }) {
  const list = smart.follow_up_tests || [];
  if (list.length === 0) return <Card className="p-10 rounded-2xl text-center text-ink/60">No follow-up tests recommended.</Card>;
  const patientName = report?.patient_name || smart.patient_name || "";
  const patientPhone = report?.patient_whatsapp || report?.patient_phone || smart.patient_phone || "";
  const reportUrl = typeof window !== "undefined" ? window.location.href : "";
  return (
    <Card className="p-6 rounded-2xl">
      <div className="font-heading font-semibold text-ink text-lg">Recommended Follow-up Tests</div>
      <p className="text-sm text-ink/60 mt-1">AI-suggested based on your findings. Every booking stays with {lab?.name || "your lab"}.</p>
      <div className="mt-4 grid sm:grid-cols-2 gap-3">
        {list.map((t, i) => (
          <div key={i} className="p-4 rounded-xl border border-border card-hover flex flex-col">
            <div className="font-heading font-semibold text-ink">{t.name}</div>
            <div className="text-xs text-ink/60 mt-1 flex-1">{t.reason}</div>
            <div className="mt-3 flex justify-between items-center">
              <span className="text-xs text-efgh-green font-medium">{t.frequency}</span>
              <BookNowButton
                labId={report?.lab_id || lab?.id}
                lab={lab}
                report={report}
                tests={[t.name]}
                patientName={patientName}
                patientPhone={patientPhone}
                reportUrl={reportUrl}
                testid={`book-followup-${i}`}
                size="sm"
              />
            </div>
          </div>
        ))}
      </div>
      {/* Bulk book — send all recommended tests at once */}
      {list.length > 1 && (
        <div className="mt-4 flex justify-end">
          <BookNowButton
            labId={report?.lab_id || lab?.id}
            lab={lab}
            report={report}
            tests={list.map((x) => x.name)}
            patientName={patientName}
            patientPhone={patientPhone}
            reportUrl={reportUrl}
            testid="book-followup-all"
            size="sm"
            variant="secondary"
          >
            Book all {list.length}
          </BookNowButton>
        </div>
      )}
    </Card>
  );
}

/* ============ SHARED ============ */
function StatMini({ bg, fg, val, label, icon }) {
  return (
    <div className={`rounded-xl p-3 sm:p-4 min-w-0 ${bg}`}>
      <div className="text-xl sm:text-2xl">{icon}</div>
      <div className={`mt-1 font-heading font-bold text-2xl sm:text-3xl ${fg}`}>{val}</div>
      <div className="text-[11px] sm:text-xs text-ink/70 leading-tight">{label}</div>
    </div>
  );
}
function Advice({ title, icon: Icon, color, bg, items }) {
  if (!items || items.length === 0) return null;
  return (
    <Card className="p-5 rounded-2xl">
      <div className={`w-10 h-10 rounded-lg ${bg} ${color} grid place-items-center`}><Icon className="w-5 h-5" /></div>
      <div className="mt-3 font-heading font-semibold text-ink">{title}</div>
      <ul className="mt-3 space-y-2 text-sm text-ink/70 list-disc list-inside">
        {items.map((it, i) => <li key={i}>{it}</li>)}
      </ul>
    </Card>
  );
}

/* ============ BIOMARKER DRILL-DOWN MODAL ============ */
export function BiomarkerModal({ bio, history, onClose }) {
  const seriesData = useMemo(() => {
    if (!bio || !history?.length) return [];
    return history.map((r) => {
      const m = (r.biomarkers || []).find((x) => x.name === bio.name);
      const v = m ? parseFloat(m.value) : null;
      return { date: (r.report_date || r.created_at || "").slice(0, 10), value: isNaN(v) ? null : v };
    }).filter((d) => d.value !== null);
  }, [bio, history]);

  const range = useMemo(() => parseRange(bio?.reference_range), [bio]);
  const val = bio ? parseFloat(bio.value) : NaN;
  const pos = range ? rangePosition(val, range) : null;

  return (
    <Dialog open={!!bio} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="biomarker-modal">
        <DialogTitle className="font-heading text-xl">{bio?.name}</DialogTitle>
        {bio && (
          <div className="space-y-5">
            <div className="flex items-baseline gap-3">
              <div className="font-heading font-bold text-4xl" style={{ color: statusColor(bio.status) }}>
                {bio.value}
                <span className="text-base text-ink/50 font-normal ml-1">{bio.unit}</span>
              </div>
              <span className={`pill pill-${bio.status}`}>{String(bio.status).toUpperCase()}</span>
            </div>
            <div className="text-sm text-ink/70">
              <b>Reference range:</b> {bio.reference_range} · <b>Category:</b> {bio.category}
            </div>
            {Array.isArray(bio.all_reference_ranges) && bio.all_reference_ranges.length >= 2 && (
              <div className="rounded-lg border border-border p-3" data-testid={`all-ranges-${bio.code || bio.name}`}>
                <div className="text-[11px] font-semibold uppercase tracking-wide text-ink/60 mb-2">
                  All applicable reference ranges
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 text-[12px]">
                  {bio.all_reference_ranges.map((r) => (
                    <div key={r.label} className="flex items-center justify-between gap-2 py-1 border-b last:border-b-0 border-border/40">
                      <span className="text-ink/70">{r.label}</span>
                      <span className="font-medium text-ink">{r.display}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {range && pos !== null && <RangeBar range={range} value={val} unit={bio.unit} status={bio.status} pos={pos} />}
            {bio.interpretation && (
              <div className="p-3 rounded-lg bg-muted text-sm text-ink/80">
                <b>What this means:</b> {bio.interpretation}
              </div>
            )}
            {seriesData.length > 1 ? (
              <div>
                <div className="text-sm font-medium text-ink mb-2">Trend across your reports</div>
                <div className="h-52">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={seriesData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#EEF0F4" />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} width={40} />
                      <Tooltip />
                      {range && <ReferenceArea y1={range.min} y2={range.max} strokeOpacity={0} fill="#4CAF50" fillOpacity={0.08} />}
                      <Line type="monotone" dataKey="value" stroke={statusColor(bio.status)} strokeWidth={2.5} dot={{ r: 4 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            ) : (
              <div className="text-sm text-ink/50">Historical trend will appear once you upload more reports containing this biomarker.</div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function parseRangeLocal(ref) {
  return parseRange(ref);
}

/* ============ ASK AI DRAWER ============ */
function AskAIDrawer({ open, onClose, reportId }) {
  const [q, setQ] = useState("");
  const [chat, setChat] = useState([]);
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [chat, busy]);

  const send = async (q0) => {
    const question = (q0 || q).trim(); if (!question) return;
    setChat((c) => [...c, { role: "user", content: question }]); setQ(""); setBusy(true);
    try {
      const r = await api.post(`/reports/${reportId}/chat`, { report_id: reportId, message: question });
      setChat((c) => [...c, { role: "assistant", content: r.data.answer }]);
    } catch { toast.error("Ask AI failed"); }
    finally { setBusy(false); }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex" data-testid="ask-ai-drawer">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <div className="w-full max-w-md bg-white border-l border-border flex flex-col animate-fade-up">
        <div className="p-4 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-2 font-heading font-semibold text-ink">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-efgh-orange via-efgh-yellow to-efgh-green grid place-items-center text-white"><Sparkles className="w-4 h-4" /></div>
            Ask AI about your report
          </div>
          <button onClick={onClose} className="text-ink/50 hover:text-ink" data-testid="close-ask-ai"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4 flex flex-wrap gap-2 border-b border-border">
          {["What does elevated blood glucose mean?", "Foods for liver health?", "Explain my potassium result", "Why is WBC low?"].map((s) => (
            <button key={s} onClick={() => send(s)} className="text-xs px-3 py-1.5 rounded-full bg-muted hover:bg-ink hover:text-white transition-all">{s}</button>
          ))}
        </div>
        <div ref={scrollRef} className="flex-1 overflow-auto p-4 space-y-3">
          {chat.length === 0 && <div className="text-sm text-ink/50 text-center py-10">Ask any question about your results. AI answers reference your own values.</div>}
          {chat.map((m, i) => (
            <div key={i} className={`p-3 rounded-xl text-sm ${m.role === "user" ? "bg-muted ml-8" : "bg-gradient-to-br from-efgh-green/10 to-efgh-blue/10 mr-8"}`}>
              <div className="font-medium mb-1 text-xs uppercase tracking-widest text-ink/50">{m.role === "user" ? "You" : "EFGH AI"}</div>
              <div className="whitespace-pre-line text-ink/85">{m.content}</div>
            </div>
          ))}
          {busy && <div className="text-sm text-ink/50"><Loader2 className="w-4 h-4 inline animate-spin mr-2" /> Thinking…</div>}
        </div>
        <form onSubmit={(e) => { e.preventDefault(); send(); }} className="p-4 border-t border-border flex gap-2">
          <Input data-testid="chat-input" placeholder="Ask anything about your results…" value={q} onChange={(e) => setQ(e.target.value)} />
          <Button data-testid="chat-send" type="submit" disabled={busy || !q.trim()} className="bg-ink hover:bg-ink/90 text-white">
            <Send className="w-4 h-4" />
          </Button>
        </form>
      </div>
    </div>
  );
}


function SectionNav({ smart, history, className = "" }) {
  const items = [
    { id: "section-summary", label: "Smart Health Summary", show: true, icon: "✨" },
    { id: "section-bodymap", label: "Overall Health Snapshot", show: (smart?.biomarkers || []).length > 0 && (smart?.health_snapshot || []).some((h) => h.status !== "not_tested"), icon: "🫀" },
    { id: "section-biomarkers", label: "Biomarker Details", show: (smart?.biomarkers || []).length > 0, icon: "🔬" },
    { id: "section-critical", label: "Critical Findings", show: (smart?.critical_findings || []).length > 0, icon: "⚠" },
    { id: "section-trends", label: "Compare to Past", show: (history || []).length > 0, icon: "📈" },
    { id: "section-lifestyle", label: "Lifestyle & Food", show: (smart?.diet_recommendations?.length || smart?.physical_activity?.length || smart?.avoid_watch_out?.length) > 0, icon: "🥗" },
    { id: "section-followup", label: "Book Follow-Up", show: (smart?.follow_up_tests || []).length > 0, icon: "📅" },
  ].filter((i) => i.show);
  const [active, setActive] = useState(items[0]?.id);
  useEffect(() => {
    const handler = () => {
      const y = window.scrollY + 140;
      let cur = items[0]?.id;
      for (const it of items) {
        const el = document.querySelector(`[data-testid="${it.id}"]`);
        if (el && el.offsetTop <= y) cur = it.id;
      }
      setActive(cur);
    };
    window.addEventListener("scroll", handler, { passive: true });
    handler();
    return () => window.removeEventListener("scroll", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length]);
  const jump = (id) => {
    const el = document.querySelector(`[data-testid="${id}"]`);
    if (el) window.scrollTo({ top: el.offsetTop - 90, behavior: "smooth" });
  };
  return (
    <aside data-testid="section-nav" className={`hidden lg:block self-start sticky top-24 ${className}`}>
      <div className="text-[10px] uppercase tracking-[0.22em] text-ink/40 font-heading font-semibold mb-2 px-2">On this page</div>
      <nav className="flex flex-col gap-0.5">
        {items.map((i) => (
          <button
            key={i.id}
            data-testid={`nav-${i.id}`}
            onClick={() => jump(i.id)}
            className={`text-left text-xs px-3 py-2 rounded-lg transition-all flex items-center gap-2 ${
              active === i.id ? "bg-ink text-white font-semibold" : "text-ink/70 hover:bg-muted"
            }`}
          >
            <span className="text-sm">{i.icon}</span><span className="truncate">{i.label}</span>
          </button>
        ))}
      </nav>
    </aside>
  );
}


/* ==================== LAB LETTERHEAD ==================== */
export function PatientMetaCard({ report, smart, healthScore, band, onAskAI, showMismatch = false, hideInteractive = false }) {
  // Human-friendly labels for known metadata keys — merged from both report doc + smart.patient_meta
  const LABELS = {
    patient_name: "Patient Name",
    age_gender: "Age / Gender",
    uhid: "UHID",
    client_code: "Client / Code",
    referring_doctor: "Ref Doctor",
    visit_id: "Visit ID",
    lab_reg_no: "Lab ID / Sample No",
    barcode_no: "Barcode No",
    collected_date: "Collected Date",
    reported_date: "Reported Date",
    report_date: "Report Date",
    accession_no: "Accession No",
    order_id: "Order ID",
    branch: "Branch",
    department: "Department",
    consulting_doctor: "Consulting Doctor",
    pathologist: "Pathologist",
    package_name: "Package",
    report_status: "Report Status",
  };
  const meta = { ...(smart?.patient_meta || {}), ...report };
  const age = meta.patient_age ? `${meta.patient_age}Y` : (meta.age || null);
  const gender = meta.patient_gender || meta.gender || null;
  const age_gender = [age, gender].filter(Boolean).join(" · ") || null;
  const rows = [
    ["patient_name", meta.patient_name],
    ["age_gender", age_gender],
    ["uhid", meta.uhid],
    ["client_code", meta.client_code],
    ["referring_doctor", meta.referring_doctor],
    ["visit_id", meta.visit_id],
    ["lab_reg_no", meta.lab_reg_no],
    ["barcode_no", meta.barcode_no],
    ["collected_date", meta.collected_date],
    ["reported_date", meta.reported_date || meta.report_date],
    ["package_name", meta.package_name],
    ["report_status", (meta.status || "").replace(/_/g, " ").toUpperCase() || null],
  ].filter(([, v]) => v !== null && v !== undefined && v !== "");
  // Any additional keys the AI extracted that we don't have a label for — still show them
  const knownKeys = new Set(rows.map((r) => r[0]).concat(["patient_age", "patient_gender", "age", "gender", "id", "smart_report", "created_at", "updated_at", "lab_id", "family_member_id", "family_member_name", "category", "patient_id", "patient_whatsapp", "reviewer_signatures", "authorized_reviewer_ids", "audio_en_url", "audio_hi_url", "has_audio_en", "has_audio_hi", "reviewer_name", "reviewed_at", "name_mismatch", "parsed_patient_name", "health_score", "health_band", "status", "report_date"]));
  Object.entries(smart?.patient_meta || {}).forEach(([k, v]) => {
    if (!knownKeys.has(k) && v !== null && v !== undefined && v !== "") {
      const label = LABELS[k] || k.replace(/[_-]/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
      rows.push([k, v, label]);
    }
  });

  const scoreDisplay = typeof healthScore === "number" ? healthScore : "—";

  return (
    <div className="mb-6 rounded-2xl border border-border bg-white overflow-hidden print:rounded-none print:mb-4 print:border-ink/30" data-testid="patient-meta-card">
      <div className="grid md:grid-cols-[1fr_auto] gap-4 p-4 md:p-5">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-ink/40 font-heading font-semibold mb-2">Patient &amp; Report Details</div>
          {showMismatch && report?.name_mismatch && (
            <div className="mb-3 px-3 py-2 rounded-lg bg-danger/10 border border-danger/30 text-xs text-danger font-medium print:hidden" data-testid="name-mismatch-flag">
              ⚠ Name mismatch: uploaded &quot;{report.patient_name}&quot; · parsed &quot;{report.parsed_patient_name}&quot;. Please verify with the lab.
            </div>
          )}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-2 text-xs">
            {rows.map(([key, v, customLabel]) => (
              <div key={key} className="min-w-0" data-testid={`meta-${key.replace(/\W+/g, "-").toLowerCase()}`}>
                <div className="text-ink/45 uppercase tracking-wider text-[10px]">{customLabel || LABELS[key] || key}</div>
                <div className="text-ink font-medium truncate mt-0.5">{v}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="flex md:flex-col items-center md:items-end gap-3 md:gap-2 md:min-w-[140px] md:border-l md:border-border md:pl-5">
          <div className="text-center md:text-right">
            <div className="text-[10px] uppercase tracking-widest text-ink/40 font-heading font-semibold">Health Score</div>
            <div className="mt-1 font-heading font-bold text-4xl text-ink" data-testid="health-score-value">{scoreDisplay}<span className="text-lg text-ink/40 font-normal">/100</span></div>
            <div className={`mt-0.5 text-xs font-semibold`} style={{ color: band?.color }} data-testid="health-band">{band?.label || "—"}</div>
          </div>
          <button
            type="button"
            data-testid="open-ask-ai"
            onClick={onAskAI}
            className="ml-auto md:ml-0 md:w-full h-9 px-3 rounded-lg bg-gradient-to-r from-efgh-orange via-efgh-yellow to-efgh-green text-ink text-xs font-semibold hover:opacity-90 print:hidden"
            style={hideInteractive ? { display: "none" } : undefined}
          >
            Ask AI ✨
          </button>
        </div>
      </div>
    </div>
  );
}

export function ReportSection({ title, testid, children }) {
  return (
    <section
      data-testid={testid}
      data-section-wrap
      className="scroll-mt-20 min-w-0"
    >
      <h2 className="font-heading font-bold text-xl md:text-2xl text-ink mb-3 pb-1 border-b border-ink/20 print:text-lg print:mb-2 print:mt-1">
        {title}
      </h2>
      {children}
    </section>
  );
}

export function getLetterhead(lab) {
  if (!lab) return null;
  const lh = {
    logo_size: "md", primary_color: "#0B7A4D", accent_color: "#F4B400",
    // Print page size — A4 (default), Letter, Legal
    page_size: "A4",
    // Legacy 2-zone border (kept for backward compat)
    border_top_thickness: 0, border_bottom_thickness: 1, border_color: "#0B7A4D",
    border_style: "solid",       // solid | dashed | dotted | double | groove
    // Phase F — 4-zone border placements (above/below header, above/below footer)
    border_above_header_thickness: null,   // null → falls back to legacy border_top_thickness
    border_below_header_thickness: 0,
    border_above_footer_thickness: 0,
    border_below_footer_thickness: null,   // null → falls back to legacy border_bottom_thickness
    header_height_mm: 24, footer_height_mm: 16,
    // Phase B — Precise geometry controls (all in mm unless px)
    page_margin_top_mm: 0, page_margin_right_mm: 8, page_margin_bottom_mm: 0, page_margin_left_mm: 8,
    // Phase F — Header/footer inner padding (space between border and slot contents)
    header_padding_top_mm: 2, header_padding_bottom_mm: 2,
    footer_padding_top_mm: 2, footer_padding_bottom_mm: 2,
    // Phase F — Logo offset (fine-tune logo position independently of slot placement)
    logo_offset_x_mm: 0, logo_offset_y_mm: 0,
    section_gap_mm: 5,           // vertical gap between report sections
    slot_gap_px: 4,               // gap between stacked chips in a slot
    header_align: "start",       // start | center | end (vertical alignment inside header)
    footer_align: "end",         // start | center | end
    header_left: "logo", header_center: "name_address", header_right: "contact",
    footer_left: "address", footer_right: "signature", footer_center: "none",
    show_accreditation_logos: true, show_website_url: true,
    custom_header_text: "", custom_footer_text: "",
    font_family: "system", base_text_size: 11, text_color: "#374151", text_weight: 400,
    icon_color: "#0B7A4D", icon_size: 12, qr_color: "",
    // Phase C — watermark
    watermark_url: "",           // optional image URL rendered subtly behind content
    watermark_opacity: 0.06,
    field_styles: {},
    ...(lab.letterhead || {}),
  };
  // Resolve legacy → 4-zone aliases when unset
  if (lh.border_above_header_thickness == null) lh.border_above_header_thickness = lh.border_top_thickness ?? 0;
  if (lh.border_below_footer_thickness == null) lh.border_below_footer_thickness = lh.border_bottom_thickness ?? 0;
  return lh;
}

export function LabLetterhead({ lab, report }) {
  const lh = getLetterhead(lab);
  if (!lh) return null;
  // Per-zone borders — new schema, falls back to legacy fields for older labs.
  const primStyle = lh.primary_header_border_style || lh.border_style || "solid";
  const primColor = lh.primary_header_border_color || lh.border_color || "#0B7A4D";
  const primTopThick = lh.primary_header_border_top_thickness ?? lh.border_above_header_thickness ?? 0;
  const primBotThick = lh.primary_header_border_bottom_thickness ?? lh.border_below_header_thickness ?? 0;
  const secStyle = lh.secondary_header_border_style || lh.border_style || "solid";
  const secColor = lh.secondary_header_border_color || lh.border_color || "#0B7A4D";
  const secTopThick = lh.secondary_header_border_top_thickness ?? 1;
  const secBotThick = lh.secondary_header_border_bottom_thickness ?? 0;
  const hasSecondary = lh.secondary_header_enabled && (lh.sec_header_left || lh.sec_header_center || lh.sec_header_right);
  return (
    <div
      data-testid="lab-letterhead"
      className="relative"
      style={{
        background: lh.header_bg_color || "#ffffff",
        borderTop: primTopThick ? `${primTopThick}px ${primStyle} ${primColor}` : "none",
        borderBottom: primBotThick && !hasSecondary ? `${primBotThick}px ${primStyle} ${primColor}` : "none",
        fontFamily: LETTERHEAD_FONTS[lh.font_family] || LETTERHEAD_FONTS.system,
        color: lh.text_color || "#374151",
      }}
    >
      {lh.watermark_url && (
        <div aria-hidden style={{ position: "absolute", inset: 0, backgroundImage: `url(${lh.watermark_url})`, backgroundRepeat: "no-repeat", backgroundPosition: "center", backgroundSize: "60%", opacity: lh.watermark_opacity ?? 0.06, pointerEvents: "none" }} />
      )}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative" style={{ paddingTop: `${lh.header_padding_top_mm ?? 2}mm`, paddingBottom: `${lh.header_padding_bottom_mm ?? 2}mm` }}>
        <LetterheadHeaderContent lab={lab} report={report} lh={lh} />
      </div>
      {hasSecondary && (
        <div
          data-testid="lab-letterhead-secondary-header"
          className="relative"
          style={{
            background: lh.sec_header_bg_color || lh.header_bg_color || "#ffffff",
            borderTop: secTopThick ? `${secTopThick}px ${secStyle} ${secColor}` : "none",
            borderBottom: secBotThick ? `${secBotThick}px ${secStyle} ${secColor}` : "none",
          }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" style={{ paddingTop: `${lh.sec_header_padding_top_mm ?? 2}mm`, paddingBottom: `${lh.sec_header_padding_bottom_mm ?? 2}mm` }}>
            <LetterheadHeaderContent lab={lab} report={report} lh={lh} secondary />
          </div>
        </div>
      )}
    </div>
  );
}

/* Digital + print bottom letterhead — mirrors the top header layout, includes doctor signatures. */
export function LabLetterheadBottom({ lab, report }) {
  const lh = getLetterhead(lab);
  if (!lh) return null;
  // Per-zone borders — falls back to legacy fields for older labs.
  const primStyle = lh.primary_footer_border_style || lh.border_style || "solid";
  const primColor = lh.primary_footer_border_color || lh.border_color || "#0B7A4D";
  const primTopThick = lh.primary_footer_border_top_thickness ?? lh.border_above_footer_thickness ?? 0;
  const primBotThick = lh.primary_footer_border_bottom_thickness ?? lh.border_below_footer_thickness ?? 0;
  const secStyle = lh.secondary_footer_border_style || lh.border_style || "solid";
  const secColor = lh.secondary_footer_border_color || lh.border_color || "#0B7A4D";
  const secTopThick = lh.secondary_footer_border_top_thickness ?? 0;
  const secBotThick = lh.secondary_footer_border_bottom_thickness ?? 1;
  const hasSecondary = lh.secondary_footer_enabled && (lh.sec_footer_left || lh.sec_footer_center || lh.sec_footer_right);
  return (
    <div data-testid="lab-letterhead-bottom-wrap">
      {hasSecondary && (
        <div
          data-testid="lab-letterhead-secondary-footer"
          className="relative"
          style={{
            background: lh.sec_footer_bg_color || lh.footer_bg_color || "#ffffff",
            borderTop: secTopThick ? `${secTopThick}px ${secStyle} ${secColor}` : "none",
            borderBottom: secBotThick ? `${secBotThick}px ${secStyle} ${secColor}` : "none",
            fontFamily: LETTERHEAD_FONTS[lh.font_family] || LETTERHEAD_FONTS.system,
            color: lh.text_color || "#374151",
          }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" style={{ paddingTop: `${lh.sec_footer_padding_top_mm ?? 2}mm`, paddingBottom: `${lh.sec_footer_padding_bottom_mm ?? 2}mm` }}>
            <LetterheadFooterContent lab={lab} report={report} lh={lh} secondary />
          </div>
        </div>
      )}
      <div
        data-testid="lab-letterhead-bottom"
        style={{
          background: lh.footer_bg_color || "#ffffff",
          borderTop: primTopThick && !hasSecondary ? `${primTopThick}px ${primStyle} ${primColor}` : "none",
          borderBottom: primBotThick ? `${primBotThick}px ${primStyle} ${primColor}` : "none",
          fontFamily: LETTERHEAD_FONTS[lh.font_family] || LETTERHEAD_FONTS.system,
          color: lh.text_color || "#374151",
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" style={{ paddingTop: `${lh.footer_padding_top_mm ?? 2}mm`, paddingBottom: `${lh.footer_padding_bottom_mm ?? 2}mm` }}>
          <LetterheadFooterContent lab={lab} report={report} lh={lh} />
        </div>
      </div>
    </div>
  );
}

/* Print-only running header — repeats at the top of every printed page via position:fixed. */
export function PrintRunningHeader({ lab, report }) {
  const lh = getLetterhead(lab);
  if (!lh) return null;
  const primStyle = lh.primary_header_border_style || lh.border_style || "solid";
  const primColor = lh.primary_header_border_color || lh.border_color || "#0B7A4D";
  const primTopThick = lh.primary_header_border_top_thickness ?? lh.border_above_header_thickness ?? 0;
  const primBotThick = lh.primary_header_border_bottom_thickness ?? lh.border_below_header_thickness ?? 0;
  const secStyle = lh.secondary_header_border_style || lh.border_style || "solid";
  const secColor = lh.secondary_header_border_color || lh.border_color || "#0B7A4D";
  const secTopThick = lh.secondary_header_border_top_thickness ?? 1;
  const secBotThick = lh.secondary_header_border_bottom_thickness ?? 0;
  const mL = lh.page_margin_left_mm ?? 8;
  const mR = lh.page_margin_right_mm ?? 8;
  return (
    <div
      data-testid="print-running-header"
      className="print-running-header"
      style={{
        background: lh.header_bg_color || "#ffffff",
        borderTop: primTopThick ? `${primTopThick}px ${primStyle} ${primColor}` : "none",
        borderBottom: primBotThick ? `${primBotThick}px ${primStyle} ${primColor}` : "none",
        minHeight: `${lh.header_height_mm}mm`,
        fontFamily: LETTERHEAD_FONTS[lh.font_family] || LETTERHEAD_FONTS.system,
        color: lh.text_color || "#374151",
      }}
    >
      <div className="overflow-hidden" style={{ paddingLeft: `${mL}mm`, paddingRight: `${mR}mm`, paddingTop: `${lh.header_padding_top_mm ?? 2}mm`, paddingBottom: `${lh.header_padding_bottom_mm ?? 2}mm` }}>
        <LetterheadHeaderContent lab={lab} report={report} lh={lh} compact />
      </div>
      {lh.secondary_header_enabled && (lh.sec_header_left || lh.sec_header_center || lh.sec_header_right) && (
        <div style={{ background: lh.sec_header_bg_color || lh.header_bg_color || "#ffffff", borderTop: secTopThick ? `${secTopThick}px ${secStyle} ${secColor}` : "none", borderBottom: secBotThick ? `${secBotThick}px ${secStyle} ${secColor}` : "none", paddingLeft: `${mL}mm`, paddingRight: `${mR}mm`, paddingTop: `${lh.sec_header_padding_top_mm ?? 2}mm`, paddingBottom: `${lh.sec_header_padding_bottom_mm ?? 2}mm` }}>
          <LetterheadHeaderContent lab={lab} report={report} lh={lh} compact secondary />
        </div>
      )}
    </div>
  );
}

/* Print-only running footer — repeats at the bottom of every printed page via position:fixed. */
export function PrintRunningFooter({ lab, report }) {
  const lh = getLetterhead(lab);
  if (!lh) return null;
  const primStyle = lh.primary_footer_border_style || lh.border_style || "solid";
  const primColor = lh.primary_footer_border_color || lh.border_color || "#0B7A4D";
  const primTopThick = lh.primary_footer_border_top_thickness ?? lh.border_above_footer_thickness ?? 0;
  const primBotThick = lh.primary_footer_border_bottom_thickness ?? lh.border_below_footer_thickness ?? 0;
  const secStyle = lh.secondary_footer_border_style || lh.border_style || "solid";
  const secColor = lh.secondary_footer_border_color || lh.border_color || "#0B7A4D";
  const secTopThick = lh.secondary_footer_border_top_thickness ?? 0;
  const secBotThick = lh.secondary_footer_border_bottom_thickness ?? 1;
  const mL = lh.page_margin_left_mm ?? 8;
  const mR = lh.page_margin_right_mm ?? 8;
  return (
    <div
      data-testid="print-running-footer"
      className="print-running-footer"
      style={{
        background: lh.footer_bg_color || "#ffffff",
        borderTop: primTopThick ? `${primTopThick}px ${primStyle} ${primColor}` : "none",
        borderBottom: primBotThick ? `${primBotThick}px ${primStyle} ${primColor}` : "none",
        minHeight: `${lh.footer_height_mm}mm`,
        fontFamily: LETTERHEAD_FONTS[lh.font_family] || LETTERHEAD_FONTS.system,
        color: lh.text_color || "#374151",
      }}
    >
      {lh.secondary_footer_enabled && (lh.sec_footer_left || lh.sec_footer_center || lh.sec_footer_right) && (
        <div style={{ background: lh.sec_footer_bg_color || lh.footer_bg_color || "#ffffff", borderTop: secTopThick ? `${secTopThick}px ${secStyle} ${secColor}` : "none", borderBottom: secBotThick ? `${secBotThick}px ${secStyle} ${secColor}` : "none", paddingLeft: `${mL}mm`, paddingRight: `${mR}mm`, paddingTop: `${lh.sec_footer_padding_top_mm ?? 2}mm`, paddingBottom: `${lh.sec_footer_padding_bottom_mm ?? 2}mm` }}>
          <LetterheadFooterContent lab={lab} report={report} lh={lh} compact secondary />
        </div>
      )}
      <div className="overflow-hidden" style={{ paddingLeft: `${mL}mm`, paddingRight: `${mR}mm`, paddingTop: `${lh.footer_padding_top_mm ?? 2}mm`, paddingBottom: `${lh.footer_padding_bottom_mm ?? 2}mm` }}>
        <LetterheadFooterContent lab={lab} report={report} lh={lh} compact />
      </div>
    </div>
  );
}

/* Font family map — resolves letterhead `font_family` code into a real CSS font-family stack.
   Extended in Feb 2026 with 15+ Google-Font-backed families to give lab admins richer typography. */
export const LETTERHEAD_FONTS = {
  system: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  sans: "'Inter', 'Helvetica Neue', Arial, sans-serif",
  serif: "'Merriweather', Georgia, 'Times New Roman', serif",
  mono: "'JetBrains Mono', Menlo, Monaco, Consolas, monospace",
  heading: "'Playfair Display', Georgia, serif",
  display: "'Poppins', 'Segoe UI', sans-serif",
  roboto: "'Roboto', 'Helvetica Neue', Arial, sans-serif",
  open_sans: "'Open Sans', 'Segoe UI', Arial, sans-serif",
  lato: "'Lato', 'Helvetica Neue', Arial, sans-serif",
  montserrat: "'Montserrat', 'Helvetica Neue', Arial, sans-serif",
  nunito: "'Nunito', 'Helvetica Neue', Arial, sans-serif",
  source_sans: "'Source Sans 3', 'Segoe UI', Arial, sans-serif",
  dm_sans: "'DM Sans', 'Segoe UI', Arial, sans-serif",
  work_sans: "'Work Sans', 'Segoe UI', Arial, sans-serif",
  ibm_plex_sans: "'IBM Plex Sans', 'Segoe UI', Arial, sans-serif",
  manrope: "'Manrope', 'Segoe UI', Arial, sans-serif",
  lora: "'Lora', Georgia, serif",
  pt_serif: "'PT Serif', Georgia, serif",
  cormorant: "'Cormorant Garamond', Georgia, serif",
  space_grotesk: "'Space Grotesk', 'Segoe UI', Arial, sans-serif",
  bebas_neue: "'Bebas Neue', 'Impact', 'Arial Narrow Bold', sans-serif",
};

/* Human-readable labels for the studio font-picker (same order as font selector). */
export const LETTERHEAD_FONT_LABELS = {
  system: "System default",
  sans: "Inter (Sans)",
  serif: "Merriweather (Serif)",
  heading: "Playfair Display",
  display: "Poppins",
  mono: "JetBrains Mono",
  roboto: "Roboto",
  open_sans: "Open Sans",
  lato: "Lato",
  montserrat: "Montserrat",
  nunito: "Nunito",
  source_sans: "Source Sans 3",
  dm_sans: "DM Sans",
  work_sans: "Work Sans",
  ibm_plex_sans: "IBM Plex Sans",
  manrope: "Manrope",
  lora: "Lora (Serif)",
  pt_serif: "PT Serif",
  cormorant: "Cormorant Garamond",
  space_grotesk: "Space Grotesk",
  bebas_neue: "Bebas Neue",
};

/* Compute the resolved style (family/size/color/weight) for a given field slot,
   merging per-field override on top of global letterhead defaults. compact=print variant. */
function fieldStyle(lh, fieldKey, compact) {
  const global = {
    fontFamily: LETTERHEAD_FONTS[lh.font_family] || LETTERHEAD_FONTS.system,
    fontSize: `${(lh.base_text_size ?? 11) * (compact ? 0.85 : 1)}px`,
    color: lh.text_color || "#374151",
    fontWeight: lh.text_weight ?? 400,
  };
  const fs = (lh.field_styles || {})[fieldKey];
  if (!fs) return global;
  return {
    fontFamily: fs.family ? (LETTERHEAD_FONTS[fs.family] || fs.family) : global.fontFamily,
    fontSize: fs.size ? `${fs.size * (compact ? 0.85 : 1)}px` : global.fontSize,
    color: fs.color || global.color,
    fontWeight: fs.weight ?? global.fontWeight,
  };
}

/* Small helper: render a Lucide icon with the letterhead's icon_color and icon_size.
   Per-field icon overrides live in lh.field_styles[fieldKey].icon_{color,size,hidden}. */
function LhIcon({ icon: Icon, lh, compact, fieldKey }) {
  const fs = fieldKey ? (lh.field_styles || {})[fieldKey] : null;
  if (fs && fs.icon_hidden) return null;
  const sz = ((fs && fs.icon_size) ?? lh.icon_size ?? 12) * (compact ? 0.85 : 1);
  const color = (fs && fs.icon_color) || lh.icon_color || "#0B7A4D";
  return <Icon style={{ color, width: sz, height: sz, display: "inline", verticalAlign: "-1px", marginRight: 4 }} strokeWidth={2} />;
}

/* Shared letterhead slot renderer — handles all the generic contact/registration/logo/QR options
   that are valid in both header and footer. Returns undefined for cases that need slot-specific
   rendering (name_address for header, signatures for footer). */
function renderSharedSlot(choice, { lab, report, lh, compact, isFooter, logoClass }) {
  const accredLogos = lab.accreditation_logos || [];
  const customLogos = lab.custom_logos || [];
  const socials = lab.social_handles || [];
  const st = (k) => fieldStyle(lh, k, compact);
  switch (choice) {
    case "logo": {
      const dx = lh.logo_offset_x_mm ?? 0;
      const dy = lh.logo_offset_y_mm ?? 0;
      // NEW: explicit pixel height with legacy sm/md/lg fallback. Compact
      // (bottom letterhead) automatically scales at 70% to keep proportions.
      const explicitPx = Number.isFinite(lh.logo_height_px) ? lh.logo_height_px : null;
      const effectivePx = explicitPx ? (compact ? Math.round(explicitPx * 0.72) : explicitPx) : null;
      const alignClass = { left: "justify-start", center: "justify-center", right: "justify-end" }[lh.logo_align || "left"] || "justify-start";
      const imgStyle = effectivePx
        ? { height: `${effectivePx}px`, maxWidth: `${effectivePx * 3.2}px`, transform: (dx || dy) ? `translate(${dx}mm, ${dy}mm)` : undefined }
        : { transform: (dx || dy) ? `translate(${dx}mm, ${dy}mm)` : undefined };
      const fallbackClass = effectivePx ? "" : (logoClass || (compact ? "h-8 max-w-[110px]" : "h-14 max-w-[160px]"));
      const inner = lab.logo_url
        ? <img src={assetUrl(lab.logo_url, 800)} alt={lab.name} className={`${fallbackClass} object-contain`} style={imgStyle} />
        : <div className={`${compact ? "w-8 h-8 text-sm" : "w-14 h-14 text-xl"} rounded-xl grid place-items-center font-heading font-bold text-white`} style={{ background: lh.primary_color, ...imgStyle }}>{(lab.name || "L")[0]}</div>;
      return <div className={`flex items-center w-full ${alignClass}`}>{inner}</div>;
    }
    case "name":
      return <div style={{ ...st("name"), color: lh.primary_color, fontFamily: (lh.field_styles?.name?.family && LETTERHEAD_FONTS[lh.field_styles.name.family]) || LETTERHEAD_FONTS[lh.font_family] || LETTERHEAD_FONTS.heading, fontSize: `${(lh.field_styles?.name?.size ?? (compact ? 14 : 20)) * (compact ? 0.85 : 1)}px`, fontWeight: lh.field_styles?.name?.weight ?? 700 }} className="truncate">{lab.name}</div>;
    case "contact":
      return (
        <div style={st("contact")} className={isFooter ? "" : "text-right"}>
          {lab.phone && <div><LhIcon icon={Phone} lh={lh} compact={compact} fieldKey="phone" />{lab.phone}</div>}
          {lab.email && <div><LhIcon icon={Mail} lh={lh} compact={compact} fieldKey="email" />{lab.email}</div>}
          {lh.show_website_url && lab.website && <div><LhIcon icon={Globe} lh={lh} compact={compact} fieldKey="website" />{lab.website.replace(/^https?:\/\//, "")}</div>}
        </div>
      );
    case "phone":
      return lab.phone ? <div style={st("phone")}><LhIcon icon={Phone} lh={lh} compact={compact} fieldKey="phone" />{lab.phone}</div> : null;
    case "email":
      return lab.email ? <div style={st("email")}><LhIcon icon={Mail} lh={lh} compact={compact} fieldKey="email" />{lab.email}</div> : null;
    case "website":
      return lab.website ? <div style={st("website")}><LhIcon icon={Globe} lh={lh} compact={compact} fieldKey="website" />{lab.website.replace(/^https?:\/\//, "")}</div> : null;
    case "whatsapp":
      return lab.whatsapp_number ? <div style={st("whatsapp")}><LhIcon icon={MessageCircle} lh={lh} compact={compact} fieldKey="whatsapp" />WhatsApp: {lab.whatsapp_number}</div> : null;
    case "customer_care":
      return lab.customer_care ? <div style={st("customer_care")}><LhIcon icon={Headphones} lh={lh} compact={compact} fieldKey="customer_care" />Customer care: {lab.customer_care}</div> : null;
    case "toll_free":
      return lab.toll_free ? <div style={st("toll_free")}><LhIcon icon={PhoneCall} lh={lh} compact={compact} fieldKey="toll_free" />Toll-free: {lab.toll_free}</div> : null;
    case "emergency":
      return lab.emergency_contact ? <div style={{ ...st("emergency"), color: st("emergency").color === "#374151" ? "#B91C1C" : st("emergency").color }}><LhIcon icon={AlertTriangle} lh={lh} compact={compact} fieldKey="emergency" />Emergency: {lab.emergency_contact}</div> : null;
    case "working_hours":
      return lab.working_hours ? <div style={st("working_hours")}><LhIcon icon={Clock} lh={lh} compact={compact} fieldKey="working_hours" />{lab.working_hours}</div> : null;
    case "address":
      return (lab.address || lab.city) ? <div style={st("address")}><LhIcon icon={MapPin} lh={lh} compact={compact} fieldKey="address" />{lab.address}{lab.city ? `, ${lab.city}` : ""}</div> : null;
    case "license":
      return <div style={st("license")}>Lab License: {lab.license_no || "—"}</div>;
    case "gst":
      return lab.gst_number ? <div style={st("gst")}>GST: {lab.gst_number}</div> : null;
    case "cin":
      return lab.cin_number ? <div style={st("cin")}>CIN: {lab.cin_number}</div> : null;
    case "registration":
      return lab.registration_no ? <div style={st("registration")}>Reg. No.: {lab.registration_no}</div> : null;
    case "accreditation":
      return lh.show_accreditation_logos && accredLogos.length > 0 ? (
        <div className="flex items-center gap-2 flex-wrap">
          {accredLogos.map((a, i) => {
            const h = Number.isFinite(a.height_px) ? a.height_px : (compact ? 20 : 36);
            return <img key={i} src={assetUrl(a.logo_url, Math.max(200, h * 4))} alt={a.label} title={a.label} style={{ height: `${h}px`, maxWidth: `${h * 2}px`, objectFit: "contain" }} />;
          })}
        </div>
      ) : null;
    case "custom_logos":
      return customLogos.length > 0 ? (
        <div className="flex items-center gap-2 flex-wrap" data-testid="letterhead-custom-logos">
          {customLogos.map((c, i) => {
            const h = Number.isFinite(c.height_px) ? c.height_px : (compact ? 20 : 36);
            return <img key={c.id || i} src={assetUrl(c.logo_url, Math.max(200, h * 4))} alt={c.label} title={c.label} style={{ height: `${h}px`, maxWidth: `${h * 2.5}px`, objectFit: "contain" }} />;
          })}
        </div>
      ) : null;
    case "social":
      return socials.length > 0 ? (
        <div style={st("social")} className="flex flex-wrap gap-2">
          {socials.map((s, i) => <span key={i} className="inline-flex items-center"><LhIcon icon={socialIcon(s.platform)} lh={lh} compact={compact} />{s.handle || (s.url || "").replace(/^https?:\/\//, "")}</span>)}
        </div>
      ) : null;
    case "qr": {
      const reportUrl = report?.id ? `${window.location.origin}/report/${report.id}` : (lab.website || "");
      if (!reportUrl) return null;
      const s = compact ? 36 : 64;
      return (
        <div className="flex flex-col items-center" data-testid="letterhead-qr">
          <QRCodeSVG value={reportUrl} size={s} level="M" includeMargin={false} fgColor={lh.qr_color || lh.icon_color || "#000000"} />
          <div style={{ ...st("qr"), fontSize: `${(compact ? 8 : 9)}px` }}>Scan for report</div>
        </div>
      );
    }
    case "tagline":
      return <div style={{ ...st("tagline"), color: st("tagline").color === "#374151" ? lh.accent_color : st("tagline").color, fontStyle: "italic" }}>{(isFooter ? lh.custom_footer_text : lh.custom_header_text) || lab.tagline || ""}</div>;
    case "timestamp":
      return <div style={st("timestamp")}>Report generated: {(report?.reported_date || report?.report_date || "").slice(0, 10)}</div>;
    case "report_id":
      return report?.id ? <div style={st("report_id")}>Report ID: {(report.id || "").slice(0, 12).toUpperCase()}</div> : null;
    case "disclaimer":
      return <div style={{ ...st("disclaimer"), fontStyle: "italic", lineHeight: 1.3 }}>For informational purposes. Consult a qualified physician before making health decisions.</div>;
    case "custom":
      return <div style={st("custom")}>{isFooter ? lh.custom_footer_text : lh.custom_header_text}</div>;
    case "none":
      return null;
    default:
      return undefined;
  }
}

function socialIcon(p) {
  const map = { instagram: Instagram, facebook: Facebook, x: Twitter, linkedin: Linkedin, youtube: Youtube };
  return map[p] || Globe;
}

/* Check if a slot value (string or array) contains a given choice. */
function slotHas(val, choice) {
  if (Array.isArray(val)) return val.includes(choice);
  return val === choice;
}

function LetterheadHeaderContent({ lab, report, lh, compact = false, secondary = false }) {
  const logoClass = compact
    ? { sm: "h-6 max-w-[90px]", md: "h-8 max-w-[110px]", lg: "h-10 max-w-[140px]" }[lh.logo_size] || "h-8 max-w-[110px]"
    : { sm: "h-8 max-w-[120px]", md: "h-14 max-w-[160px]", lg: "h-20 max-w-[220px]" }[lh.logo_size] || "h-14 max-w-[160px]";
  const accredLogos = lab.accreditation_logos || [];

  // Slot keys — primary vs secondary come from different config fields.
  const slotL = secondary ? lh.sec_header_left : lh.header_left;
  const slotC = secondary ? lh.sec_header_center : lh.header_center;
  const slotR = secondary ? lh.sec_header_right : lh.header_right;
  const alignKeyL = secondary ? "sec_header_left" : "header_left";
  const alignKeyC = secondary ? "sec_header_center" : "header_center";
  const alignKeyR = secondary ? "sec_header_right" : "header_right";

  const renderSlot = (choice, isFooter = false) => {
    const shared = renderSharedSlot(choice, { lab, report, lh, compact, isFooter, logoClass });
    if (shared !== undefined) return shared;
    switch (choice) {
      case "name_address":
        return (
          <div className="min-w-0">
            <div className={`font-heading font-bold ${compact ? "text-sm" : "text-xl"} truncate`} style={{ color: lh.primary_color }}>{lab.name}</div>
            {!compact && lab.tagline && <div className="text-xs text-ink/60 truncate">{lab.tagline}</div>}
            {!compact && lab.address && <div className="text-xs text-ink/70 mt-1 max-w-md">{lab.address}{lab.city ? `, ${lab.city}` : ""}</div>}
          </div>
        );
      case "signature":
        return <div className={`${compact ? "text-[9px]" : "text-[11px]"} text-ink/60 text-right`}>For {lab.name}{!compact && <div className="mt-4 pt-1 border-t border-ink/30 w-32 ml-auto">Authorised Signatory</div>}</div>;
      default:
        return null;
    }
  };
  const stackSlot = (val, isFooter = false, slotAlignKey = null) => {
    const arr = Array.isArray(val) ? val : (val && val !== "none" ? [val] : []);
    if (arr.length === 0) return null;
    // Per-slot alignment: `lh.slot_align.<slotAlignKey>` = "start" | "center" | "end"
    const perSlot = (lh.slot_align || {})[slotAlignKey] || "start";
    const itemsCls = { start: "items-start", center: "items-center", end: "items-end" }[perSlot] || "items-start";
    // Per-slot layout: "row" (side-by-side) or "column" (stacked, default).
    const dir = (lh.slot_layout || {})[slotAlignKey] || "column";
    const gapX = (lh.slot_gap_x || {})[slotAlignKey] ?? (lh.slot_gap_px ?? (compact ? 6 : 10));
    const gapY = (lh.slot_gap_y || {})[slotAlignKey] ?? (lh.slot_gap_px ?? (compact ? 2 : 4));

    // 2D layout support: `__break__` sentinels split the array into visual rows.
    // Elements inside a row render side-by-side (flex-row wrap), rows stack vertically.
    const hasBreaks = arr.includes("__break__");
    if (hasBreaks) {
      const rows = arr.reduce((acc, c) => {
        if (c === "__break__") acc.push([]);
        else acc[acc.length - 1].push(c);
        return acc;
      }, [[]]).filter((r) => r.length > 0);
      return (
        <div className={`flex flex-col ${itemsCls}`} style={{ rowGap: `${gapY}px` }}>
          {rows.map((row, ri) => (
            <div key={`row-${ri}`} className={`flex flex-row flex-wrap ${itemsCls}`} style={{ columnGap: `${gapX}px`, rowGap: `${gapY}px` }}>
              {row.map((c, i) => {
                const el = renderSlot(c, isFooter);
                return el ? <div key={`${c}-${ri}-${i}`}>{el}</div> : null;
              })}
            </div>
          ))}
        </div>
      );
    }

    const wrapCls = dir === "row" ? "flex flex-row flex-wrap" : "flex flex-col";
    return <div className={`${wrapCls} ${itemsCls}`} style={{ columnGap: `${gapX}px`, rowGap: `${gapY}px` }}>{arr.map((c, i) => {
      const el = renderSlot(c, isFooter);
      return el ? <div key={`${c}-${i}`}>{el}</div> : null;
    })}</div>;
  };

  const alignClass = { start: "items-start", center: "items-center", end: "items-end" }[lh.header_align || "start"] || "items-start";
  return (
    <>
      {/* True absolute-center grid: 1fr / auto / 1fr. Left cell is right-aligned so
          its content clings to the middle divider; right cell is left-aligned for
          the mirrored effect. The center cell is auto-sized so its content is
          always dead-centre on the page regardless of what's in the sides. */}
      <div className={`grid grid-cols-[1fr_auto_1fr] gap-4 ${alignClass}`} data-testid={secondary ? "letterhead-secondary-header" : "letterhead-header"}>
        <div className="min-w-0 flex justify-start">{stackSlot(slotL, false, alignKeyL)}</div>
        <div className="min-w-0 flex justify-center text-center">{stackSlot(slotC, false, alignKeyC)}</div>
        <div className="min-w-0 flex justify-end text-right">{stackSlot(slotR, false, alignKeyR)}</div>
      </div>
      {!secondary && !compact && lh.show_accreditation_logos && accredLogos.length > 0 && !slotHas(lh.header_right, "accreditation") && !slotHas(lh.header_center, "accreditation") && !slotHas(lh.header_left, "accreditation") && (
        <div className="mt-2 pt-2 border-t border-border/60 flex justify-end items-center gap-3 flex-wrap" data-testid="letterhead-accreds">
          {accredLogos.map((a, i) => {
            const h = Number.isFinite(a.height_px) ? a.height_px : 32;
            return (
              <div key={i} className="flex flex-col items-center" title={a.label}>
                <img src={assetUrl(a.logo_url, Math.max(200, h * 4))} alt={a.label} style={{ height: `${h}px`, maxWidth: `${h * 2}px`, objectFit: "contain" }} />
                <div className="text-[9px] uppercase tracking-wider text-ink/50 mt-0.5">{a.label}</div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

/**
 * Append `?w=<size>` to a logo/signature/accreditation URL so the server-side
 * `/api/files` endpoint downscales huge originals (e.g. 3000px lab logos) to a
 * reasonable print/display size. Keeps the PDF a fraction of the size that
 * Chromium would otherwise embed on every page. Idempotent — returns the URL
 * unchanged if it's external, empty, already parameterised, or a data-uri.
 */
export function assetUrl(url, w = 600) {
  if (!url || typeof url !== "string") return url;
  if (url.startsWith("data:")) return url;
  if (!url.includes("/api/files/")) return url; // external logo host — leave untouched
  return url + (url.includes("?") ? "&" : "?") + `w=${w}`;
}

function LetterheadFooterContent({ lab, report, lh, compact = false, secondary = false }) {
  // Slot keys — primary vs secondary come from different config fields.
  const slotL = secondary ? lh.sec_footer_left : lh.footer_left;
  const slotC = secondary ? lh.sec_footer_center : lh.footer_center;
  const slotR = secondary ? lh.sec_footer_right : lh.footer_right;
  const alignKeyL = secondary ? "sec_footer_left" : "footer_left";
  const alignKeyC = secondary ? "sec_footer_center" : "footer_center";
  const alignKeyR = secondary ? "sec_footer_right" : "footer_right";

  const renderSignatures = () => {
    const attached = Array.isArray(report?.reviewer_signatures) ? report.reviewer_signatures : [];
    let sigs;
    if (attached.length > 0) sigs = attached;
    else {
      const all = lab.doctor_signatures || [];
      sigs = all.filter((s) => s.is_default);
      if (sigs.length === 0 && all.length > 0) sigs = [all[0]];
    }
    if (!sigs || sigs.length === 0) {
      return <div className={`${compact ? "text-[9px]" : "text-[11px]"} text-ink/60 text-right`}>For {lab.name}{!compact && <div className="mt-4 pt-1 border-t border-ink/30 w-32 ml-auto">Authorised Signatory</div>}</div>;
    }
    // Multiple signatures always render side-by-side — nowrap for ≤3, wrap only when > 3 to
    // avoid overflow. Each sig has its own optional `height_px` (falls back to a sensible default).
    return (
      <div className={`flex ${compact ? "gap-3" : "gap-6"} items-end justify-end ${sigs.length > 3 ? "flex-wrap" : "flex-nowrap"}`} data-testid="footer-signatures">
        {sigs.map((s, i) => {
          const h = Number.isFinite(s.height_px) ? s.height_px : (compact ? 32 : 56);
          return (
            <div key={s.id || i} className="flex flex-col items-start shrink-0" data-testid={`sig-${i}`}>
              <img src={assetUrl(s.signature_url, Math.max(300, h * 5))} alt={s.name} style={{ height: `${h}px`, maxWidth: `${h * 3.5}px`, objectFit: "contain" }} />
              <div className={`${compact ? "text-[9px]" : "text-[11px]"} font-heading font-semibold text-ink mt-0.5`}>Dr. {s.name}</div>
              {s.qualification && <div className={`${compact ? "text-[8px]" : "text-[10px]"} text-ink/60`}>{s.qualification}</div>}
              {s.registration_no && !compact && <div className="text-[9px] text-ink/50">Reg. No.: {s.registration_no}</div>}
            </div>
          );
        })}
      </div>
    );
  };
  const renderSlot = (choice) => {
    const shared = renderSharedSlot(choice, { lab, report, lh, compact, isFooter: true });
    if (shared !== undefined) return shared;
    switch (choice) {
      case "signature":
        return <div className={`${compact ? "text-[9px]" : "text-[11px]"} text-ink/60 text-right`}>For {lab.name}{!compact && <div className="mt-4 pt-1 border-t border-ink/30 w-32 ml-auto">Authorised Signatory</div>}</div>;
      case "signatures":
        return renderSignatures();
      default:
        return null;
    }
  };
  const stackSlot = (val, slotAlignKey = null) => {
    const arr = Array.isArray(val) ? val : (val && val !== "none" ? [val] : []);
    if (arr.length === 0) return null;
    const perSlot = (lh.slot_align || {})[slotAlignKey] || "start";
    const itemsCls = { start: "items-start", center: "items-center", end: "items-end" }[perSlot] || "items-start";
    const dir = (lh.slot_layout || {})[slotAlignKey] || "column";
    const gapX = (lh.slot_gap_x || {})[slotAlignKey] ?? (lh.slot_gap_px ?? (compact ? 6 : 10));
    const gapY = (lh.slot_gap_y || {})[slotAlignKey] ?? (lh.slot_gap_px ?? (compact ? 2 : 4));

    // 2D layout: __break__ sentinels split into visual rows (elements per row render side-by-side).
    const hasBreaks = arr.includes("__break__");
    if (hasBreaks) {
      const rows = arr.reduce((acc, c) => {
        if (c === "__break__") acc.push([]);
        else acc[acc.length - 1].push(c);
        return acc;
      }, [[]]).filter((r) => r.length > 0);
      return (
        <div className={`flex flex-col ${itemsCls}`} style={{ rowGap: `${gapY}px` }}>
          {rows.map((row, ri) => (
            <div key={`row-${ri}`} className={`flex flex-row flex-wrap ${itemsCls}`} style={{ columnGap: `${gapX}px`, rowGap: `${gapY}px` }}>
              {row.map((c, i) => {
                const el = renderSlot(c);
                return el ? <div key={`${c}-${ri}-${i}`}>{el}</div> : null;
              })}
            </div>
          ))}
        </div>
      );
    }

    const wrapCls = dir === "row" ? "flex flex-row flex-wrap" : "flex flex-col";
    return <div className={`${wrapCls} ${itemsCls}`} style={{ columnGap: `${gapX}px`, rowGap: `${gapY}px` }}>{arr.map((c, i) => {
      const el = renderSlot(c);
      return el ? <div key={`${c}-${i}`}>{el}</div> : null;
    })}</div>;
  };
  const primaryKeys = secondary ? ["sec_footer_left", "sec_footer_center", "sec_footer_right"] : ["footer_left", "footer_center", "footer_right"];
  const anyFooterFilled = primaryKeys.some((k) => {
    const v = lh[k]; const arr = Array.isArray(v) ? v : (v && v !== "none" ? [v] : []); return arr.length > 0;
  });
  if (!anyFooterFilled) return null;
  // Auto-inject signatures only on the primary footer, not the secondary one.
  const attached = Array.isArray(report?.reviewer_signatures) ? report.reviewer_signatures : [];
  const anySigs = attached.length > 0 || (lab.doctor_signatures || []).length > 0;
  const slotUsesSigs = ["footer_left", "footer_center", "footer_right"].some((k) => slotHas(lh[k], "signatures"));
  const showAutoSigs = !secondary && anySigs && !slotUsesSigs;
  const alignClass = { start: "items-start", center: "items-center", end: "items-end" }[lh.footer_align || "end"] || "items-end";
  return (
    <div className="w-full" data-testid={secondary ? "letterhead-secondary-footer" : "letterhead-footer"}>
      {showAutoSigs && (
        <div className={`${compact ? "mb-1" : "mb-2"} flex justify-end`} data-testid="auto-signatures">{renderSignatures()}</div>
      )}
      {/* True absolute-center grid — mirror of the header. */}
      <div className={`${compact ? "" : "pt-2"} grid grid-cols-[1fr_auto_1fr] gap-3 ${alignClass}`}>
        <div className="min-w-0 flex justify-start">{stackSlot(slotL, alignKeyL)}</div>
        <div className="min-w-0 flex justify-center text-center">{stackSlot(slotC, alignKeyC)}</div>
        <div className="min-w-0 flex justify-end text-right">{stackSlot(slotR, alignKeyR)}</div>
      </div>
    </div>
  );
}

/* SignatureFooter was retired — doctor signatures now render inside the letterhead footer via the "signatures" slot. */
